import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Modal,
  Backdrop,
  Fade,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Tooltip,
  IconButton,
  Tabs,
  Tab,
  Checkbox,
  FormControlLabel,
  Slider,
  useMediaQuery,
  Popover,
  ClickAwayListener,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,
  PlaylistAdd as PlaylistAddIcon,
  CloudUpload as CloudUploadIcon,
} from "@mui/icons-material";
import { Droppable, Draggable } from "react-beautiful-dnd";
import { useIntl, FormattedMessage } from "react-intl";
import QuestionCard from "./QuestionCard";
import SurveyPreviewSection from "./SurveyPreviewSection";
import ModernStyleFields from "./ModernStyleFields";
import { DEFAULT_GRADIENT } from "./surveyStyle";
import { SurveyProjectFields } from "./SurveyProjectFields";
import useProjectSegments from "../../hooks/useProjectSegments";
import questionSchema from '../../constants/questionSchema.json';
import DOMPurify from 'dompurify';
import * as XLSX from "xlsx";
import Papa from 'papaparse';
import { likertScaleTypes } from "../../constants/likertScales";
import { stripHtml } from "../../utils/stringUtils";

export const questionTypes = [
  { id: "single-choice", type: "SINGLE_CHOICE", label: "Single Choice", labelId: "survey_builder.single_choice" },
  { id: "multi-choice", type: "MULTI_CHOICE", label: "Multi Choice", labelId: "survey_builder.multi_choice" },
  { id: "likert-scale", type: "LIKERT_SCALE", label: "Likert Scale", labelId: "survey_builder.likert_scale" },
  { id: "open-text", type: "OPEN_TEXT", label: "Open Text", labelId: "survey_builder.open_text" },
  { id: "text", type: "TEXT", label: "Content", labelId: "survey_builder.content", style: { backgroundColor: "#e8f5e8", borderColor: "#4caf50" } },
  { id: "page-break", type: "PAGE_BREAK", label: "Page Break", labelId: "survey_builder.page_break", style: { backgroundColor: "#fff3e0", borderColor: "#ff9800" } },
];

// The Basic tab goes three-column (palette · canvas · steps) from here. Not
// MUI's lg (1200px): the app's sidebar stays open at that width, which would
// leave the canvas too narrow to edit a question in. Below it the palette
// spans the top, then the canvas and steps sit side by side (md) or stack.
const BUILDER_WIDE = "@media (min-width: 1360px)";

const stripHtmlAndTruncate = (html, maxLength = 50) => {
  if (!html) return "";
  const div = document.createElement("div");
  div.innerHTML = html;
  const text = div.textContent || div.innerText || "";
  return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
};

// Helper function to convert RGBA objects to CSS rgba() format
const rgbaToCss = (color) => {
  if (!color) return "rgba(255, 255, 255, 1)";
  if (typeof color === "string") return color;
  if (color.r !== undefined && color.g !== undefined && color.b !== undefined) {
    const alpha = color.a !== undefined ? color.a : 1;
    return `rgba(${color.r}, ${color.g}, ${color.b}, ${alpha})`;
  }
  return "rgba(255, 255, 255, 1)";
};

const toCamelCase = (str) => {
  return str
    .toLowerCase()
    .replace(/(?:^\w|[A-Z]|\b\w|\s+)/g, (match, index) =>
      index === 0 ? match.toLowerCase() : match.replace(/\s+/g, '').toUpperCase()
    )
    .replace(/\s+/g, '');
};

const getQuestionTypeLabel = (intl, questionType) =>
  intl.formatMessage({
    id: questionType.labelId,
    defaultMessage: questionType.label,
  });

function SurveyBuilder({
  steps,
  setSteps,
  currentStep,
  setCurrentStep,
  handleQuestionChange: propHandleQuestionChange,
  handleQuestionSettingsChange,
  handleGlobalSettingsChange,
  handleQuestionStylesChange,
  handleAddChoice,
  handleChoiceChange: propHandleChoiceChange,
  handleDeleteQuestion,
  handleDuplicateQuestion,
  settings,
  questionTypes,
  acceptedFiles,
  setAcceptedFiles,
  getRootProps,
  getInputProps,
  enqueueSnackbar,
  isDeleting,
  processedFileName,
  setProcessedFileName,
}) {
  const intl = useIntl();
  const [openBulkAddDialog, setOpenBulkAddDialog] = useState(false);
  const [bulkAddType, setBulkAddType] = useState("");
  const [bulkAddCount, setBulkAddCount] = useState(1);
  const [defaultQuestionText, setDefaultQuestionText] = useState("");
  const [openValidationDialog, setOpenValidationDialog] = useState(false);
  const [invalidQuestions, setInvalidQuestions] = useState([]);
  const [pendingFile, setPendingFile] = useState(null);
  const [openClearConfirmationDialog, setOpenClearConfirmationDialog] = useState(false);
  const [fileToProcess, setFileToProcess] = useState(null);
  const [deleteConfirmIndex, setDeleteConfirmIndex] = useState(null);
  // The linked project's segments, which a single-choice question can be
  // linked to; and the ones a question already is (one question per segment).
  const segments = useProjectSegments(settings.project);
  const usedSegmentIds = steps.map((s) => s.segment).filter(Boolean);
  const [activeTab, setActiveTab] = useState(0); // 0: Items, 1: Settings, 2: Preview
  const [isPublic, setIsPublic] = useState(settings.isPublic || false); // Local state for public survey
  const [showSubmissionPreview, setShowSubmissionPreview] = useState(false); // Toggle for submission preview
  const [colorPickerState, setColorPickerState] = useState({
    open: false,
    anchorEl: null,
    currentColor: "#ffffff",
    onChange: null,
    settingKey: null
  });
  const [csvGuideModalOpen, setCsvGuideModalOpen] = useState(false);
  const [likertScalesModalOpen, setLikertScalesModalOpen] = useState(false);
  const [csvConfirmationModalOpen, setCsvConfirmationModalOpen] = useState(false);
  const [csvImportData, setCsvImportData] = useState(null);
  const [expandedQuestions, setExpandedQuestions] = useState({});
  const [expandedSections, setExpandedSections] = useState({
    logo: false,
    background: true,
    questions: false,
    behavior: false,
    completion: false
  });
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const isMobile = useMediaQuery('(max-width: 600px)'); // Mobile detection
  const processingRef = useRef(false);
  const surveyAreaRef = useRef(null);

  const handleOpenBulkAddDialog = () => {
    setOpenBulkAddDialog(true);
  };

  const handleCloseBulkAddDialog = () => {
    setOpenBulkAddDialog(false);
    setBulkAddType("");
    setBulkAddCount(1);
    setDefaultQuestionText("");
  };

  // Handle adding question via dropdown selection (for mobile)
  const handleAddQuestionFromDropdown = (questionTypeId) => {
    const selectedQuestionType = questionTypes.find(qt => qt.id === questionTypeId);
    if (!selectedQuestionType) return;

    let newQuestion;

    if (selectedQuestionType.type === "PAGE_BREAK") {
      newQuestion = {
        id: `${selectedQuestionType.type}-${Date.now()}`,
        type: "PAGE_BREAK",
        choices: []
      };
    } else {
      // Unified choices structure for all question types
      let choices = [];

      if (selectedQuestionType.type === "SINGLE_CHOICE" || selectedQuestionType.type === "MULTI_CHOICE") {
        choices = [
          { text: intl.formatMessage({ id: "survey_builder.choice_1" }), value: "choice_1", index: 0, tags: [] },
          { text: intl.formatMessage({ id: "survey_builder.choice_2" }), value: "choice_2", index: 1, tags: [] },
        ];
      } else if (selectedQuestionType.type === "LIKERT_SCALE") {
        // Default Likert scale options
        const defaultOptions = [
          { text: intl.formatMessage({ id: "survey_builder.strongly_disagree" }), value: "strongly_disagree", index: 0, tags: [] },
          { text: intl.formatMessage({ id: "survey_builder.disagree" }), value: "disagree", index: 1, tags: [] },
          { text: intl.formatMessage({ id: "survey_builder.neutral" }), value: "neutral", index: 2, tags: [] },
          { text: intl.formatMessage({ id: "survey_builder.agree" }), value: "agree", index: 3, tags: [] },
          { text: intl.formatMessage({ id: "survey_builder.strongly_agree" }), value: "strongly_agree", index: 4, tags: [] },
        ];
        choices = defaultOptions;
      } else if (selectedQuestionType.type === "OPEN_TEXT" || selectedQuestionType.type === "TEXT") {
        choices = []; // No choices needed for text-based questions
      }

      newQuestion = {
        id: `${selectedQuestionType.type}-${Date.now()}`,
        type: selectedQuestionType.type,
        question: `<p>Enter your ${selectedQuestionType.label.toLowerCase()} question here</p>`,
        description: "",
        settings: {
          styles: {
            question: { fontWeight: "normal", fontStyle: "normal", fontSize: "14px", lineHeight: "1.15", letterSpacing: "0px" },
            description: { fontWeight: "normal", fontStyle: "normal", fontSize: "14px", lineHeight: "1.15", letterSpacing: "0px" },
          },
        },
        tags: [], // Ensure tags array is always initialized
        choices: choices,
        isMandatory: false,
        ...(selectedQuestionType.type === "OPEN_TEXT" && { pillar: "" }), // Initialize pillar for open text questions
      };
    }

    console.log("Adding new question via dropdown:", newQuestion);
    setSteps(prev => [...prev, newQuestion]);
    setCurrentStep(steps.length);
  };

  const handleBulkAddQuestions = () => {
    if (!bulkAddType || bulkAddCount < 1) return;
    if (bulkAddCount > 100) return;

    const newSteps = [];
    // Find the question type for label translation
    const questionType = questionTypes.find(qt => qt.type === bulkAddType);
    const questionTypeLabel = questionType ? questionType.label : bulkAddType.replace("_", " ");

    for (let i = 0; i < bulkAddCount; i++) {
      let newQuestion;
      const questionText = defaultQuestionText
        ? `<p>${DOMPurify.sanitize(defaultQuestionText)}${bulkAddCount > 1 ? ` ${i + 1}` : ""}</p>`
        : `<p>${intl.formatMessage({ id: "survey_builder.enter_question" }, { type: questionTypeLabel.toLowerCase() })} ${i + 1}</p>`;
      if (bulkAddType === "PAGE_BREAK") {
        newQuestion = {
          id: `${bulkAddType}-${Date.now() + i}`,
          type: "PAGE_BREAK",
        };
      } else {
        // Unified choices structure for all question types
        let choices = [];

        if (bulkAddType === "SINGLE_CHOICE" || bulkAddType === "MULTI_CHOICE") {
          choices = [
            { text: intl.formatMessage({ id: "survey_builder.choice_1" }), value: "choice_1", index: 0, tags: [] },
            { text: intl.formatMessage({ id: "survey_builder.choice_2" }), value: "choice_2", index: 1, tags: [] },
          ];
        } else if (bulkAddType === "LIKERT_SCALE") {
          // Default Likert scale options
          const defaultOptions = [
            { text: intl.formatMessage({ id: "survey_builder.strongly_disagree" }), value: "strongly_disagree", index: 0, tags: [] },
            { text: intl.formatMessage({ id: "survey_builder.disagree" }), value: "disagree", index: 1, tags: [] },
            { text: intl.formatMessage({ id: "survey_builder.neutral" }), value: "neutral", index: 2, tags: [] },
            { text: intl.formatMessage({ id: "survey_builder.agree" }), value: "agree", index: 3, tags: [] },
            { text: intl.formatMessage({ id: "survey_builder.strongly_agree" }), value: "strongly_agree", index: 4, tags: [] },
          ];
          choices = defaultOptions;
        } else if (bulkAddType === "OPEN_TEXT" || bulkAddType === "TEXT") {
          choices = []; // No choices needed for text-based questions
        }

        newQuestion = {
          id: `${bulkAddType}-${Date.now() + i}`,
          type: bulkAddType,
          question: questionText,
          description: "",
          settings: {
            styles: {
              question: { fontWeight: "normal", fontStyle: "normal", fontSize: "14px", lineHeight: "1.15", letterSpacing: "0px" },
              description: { fontWeight: "normal", fontStyle: "normal", fontSize: "14px", lineHeight: "1.15", letterSpacing: "0px" },
            },
          },
          tags: [], // Ensure tags array is always initialized
          choices: choices,
          isMandatory: false,
          ...(bulkAddType === "OPEN_TEXT" && { pillar: "" }), // Initialize pillar for open text questions
        };
      }
      newSteps.push(newQuestion);
    }

    console.log("Bulk adding questions:", newSteps);
    setSteps([...steps, ...newSteps]);
    setCurrentStep(steps.length);
    handleCloseBulkAddDialog();
  };

  const validateQuestions = (questions, existingSteps, schema) => {
    const errors = [];
    const questionSet = new Set(existingSteps.map(step => DOMPurify.sanitize(step.question || "").replace(/<\/?[^>]+(>|$)/g, '').trim()));
    const validQuestionTypes = questionTypes.map(qt => qt.type);
    const validScaleTypes = likertScaleTypes.map(scale => scale.id);
    const headers = questions.length > 0 ? Object.keys(questions[0]) : [];

    console.log("Headers: ", headers)
    console.log("validQuestionTypes: ", validQuestionTypes)

    // Check if 'type' header is present (required for all questions)
    if (!headers.includes('type')) {
      errors.push({
        index: 0,
        question: "File headers",
        reason: "Missing required header: type",
      });
    }

    questions.forEach((q, index) => {
      // Validate question type
      console.log("q: ", q)
      if (!q.type || !validQuestionTypes.includes(q.type.toUpperCase())) {
        errors.push({
          index: index + 1,
          question: q.question || `Question ${index + 1}`,
          reason: intl.formatMessage({ id: "survey_builder.invalid_question_type" }, { types: validQuestionTypes.join(", ") }),
        });
        return; // Skip further validation if type is invalid
      }

      const questionType = q.type.toUpperCase();

      console.log("questionType: ", questionType)

      // Get required headers for this question type
      const requiredForType = schema.requiredHeaders
        .filter(h => h.required && h.types.includes(questionType))
        .map(h => h.name);

      console.log("requiredForType: ", requiredForType)

      // Check for missing required headers
      // const missingForType = requiredForType.filter(h => !headers.includes(h));
      // console.log("missingForType: ", missingForType);
      // if (missingForType.length > 0) {
      //   errors.push({
      //     index: index + 1,
      //     question: q.question || `Question ${index + 1}`,
      //     reason: `Missing required headers for ${questionType}: ${missingForType.join(", ")}`,
      //   });
      // }

      // Validate question text for non-PAGE_BREAK and non-TEXT questions
      if (questionType !== "PAGE_BREAK" && questionType !== "TEXT") {
        if (!q.question || q.question.trim() === "" || q.question === "<p><br></p>") {
          errors.push({
            index: index + 1,
            question: q.question || `Question ${index + 1}`,
            reason: intl.formatMessage({ id: "survey_builder.missing_question_text" }),
          });
        }
      }

      // Validate choices for SINGLE_CHOICE and MULTI_CHOICE
      if (questionType === "SINGLE_CHOICE" || questionType === "MULTI_CHOICE") {
        let choices = q.choices;
        if (typeof choices === 'string') {
          choices = choices.split(',').map(text => ({ text: text.trim(), tags: [] }));
        }
        console.log("choices:", choices)
        if (!choices || !Array.isArray(choices) || choices.length < 2) {
          errors.push({
            index: index + 1,
            question: q.question || `Question ${index + 1}`,
            reason: intl.formatMessage({ id: "survey_builder.choices_required" }),
          });
        };
      }

      // Validate choices for LIKERT_SCALE
      if (questionType === "LIKERT_SCALE") {
        const choices = q.choices || [];
        if (!choices || !Array.isArray(choices) || choices.length < 2) {
          errors.push({
            index: index + 1,
            question: q.question || `Question ${index + 1}`,
            reason: intl.formatMessage({ id: "survey_builder.likert_choices_required" }),
          });
        }
      }

      // Check for duplicate questions
      const sanitizedQuestion = DOMPurify.sanitize(q.question || "").replace(/<\/?[^>]+(>|$)/g, '').trim();
      if (sanitizedQuestion && questionSet.has(sanitizedQuestion)) {
        errors.push({
          index: index + 1,
          question: q.question || `Question ${index + 1}`,
          reason: intl.formatMessage({ id: "survey_builder.duplicate_question" }),
        });
      }
      if (sanitizedQuestion) {
        questionSet.add(sanitizedQuestion);
      }
    });

    return errors;
  };

  // Helper function to parse CSV choices with proper comma handling
  const parseChoices = (choicesString) => {
    if (!choicesString || typeof choicesString !== 'string') {
      return [];
    }

    const choices = [];
    let current = '';
    let inEscape = false;

    for (let i = 0; i < choicesString.length; i++) {
      const char = choicesString[i];

      if (char === '\\' && !inEscape) {
        inEscape = true;
        continue;
      }

      if (char === ',' && !inEscape) {
        if (current.trim()) {
          choices.push(current.trim());
        }
        current = '';
        continue;
      }

      current += char;
      inEscape = false;
    }

    if (current.trim()) {
      choices.push(current.trim());
    }

    return choices;
  };

  // Helper function to check if choices match any likert scale
  const isLikertScale = (choices) => {
    if (!choices || choices.length === 0) return false;

    // Normalize choices for comparison
    const normalizedChoices = choices.map(choice => choice.toLowerCase().trim());

    // Check against each likert scale type
    for (const scaleType of likertScaleTypes) {
      if (scaleType.options && scaleType.options.length > 0) {
        const normalizedOptions = scaleType.options.map(option => option.toLowerCase().trim());

        // Check if choices match this scale (order doesn't matter, but all options should be present)
        if (normalizedChoices.length === normalizedOptions.length &&
          normalizedChoices.every(choice => normalizedOptions.includes(choice))) {
          return true;
        }
      }
    }

    return false;
  };

  // Helper function to determine question type based on choices and explicit type
  const determineQuestionType = (explicitType, choices) => {
    // If explicit type is provided, use it (after validation)
    if (explicitType) {
      const upperType = explicitType.toUpperCase();
      const validTypes = ['SINGLE_CHOICE', 'MULTI_CHOICE', 'OPEN_TEXT', 'TEXT', 'LIKERT_SCALE', 'PAGE_BREAK'];
      if (validTypes.includes(upperType)) {
        return upperType;
      }
    }

    // If no type specified and no choices or empty choices, default to OPEN_TEXT
    if (!choices || choices.length === 0) {
      return 'OPEN_TEXT';
    }

    // Check if choices match a likert scale
    if (isLikertScale(choices)) {
      return 'LIKERT_SCALE';
    }

    // Default to MULTI_CHOICE for any other choices
    return 'MULTI_CHOICE';
  };

  const processFile = async (file) => {
    if (processingRef.current || isDeleting) {
      console.log("File processing ignored: Already processing or deletion in progress");
      setAcceptedFiles([]);
      return;
    }

    processingRef.current = true;
    console.log("Starting file processing for file:", file.name);

    try {
      let questions = [];

      // Only accept CSV files
      if (file.type !== 'text/csv' && !file.name.toLowerCase().endsWith('.csv')) {
        enqueueSnackbar(intl.formatMessage({ id: "survey_builder.csv_only" }), {
          variant: "error",
          autoHideDuration: 3000,
          anchorOrigin: { vertical: "bottom", horizontal: "right" },
        });
        setAcceptedFiles([]);
        return;
      }

      const text = await file.text();
      const result = Papa.parse(text, { header: true, skipEmptyLines: true });
      if (result.errors.length > 0) {
        throw new Error(intl.formatMessage({ id: "survey_builder.csv_parsing_failed" }, { error: result.errors[0].message }));
      }
      questions = result.data;

      // Validate required columns
      if (questions.length === 0) {
        throw new Error(intl.formatMessage({ id: "survey_builder.csv_empty" }));
      }

      const headers = Object.keys(questions[0]).map(h => h.toLowerCase());
      if (!headers.includes('question')) {
        throw new Error(intl.formatMessage({ id: "survey_builder.csv_missing_question_column" }));
      }

      console.log("Processing uploaded CSV file, parsed questions:", questions);

      const normalizedQuestions = questions.map((q, index) => {
        // Check if question text is blank/empty - if so, treat as PAGE_BREAK
        const questionText = (q.question || q.Question || '').trim();
        const isBlankQuestion = !questionText;

        // If blank question, create a page break
        if (isBlankQuestion) {
          return {
            id: `PAGE_BREAK-${Date.now() + index}`,
            type: 'PAGE_BREAK',
            question: '',
            description: '',
            settings: {
              styles: {
                question: { fontWeight: "normal", fontStyle: "normal", fontSize: "14px", lineHeight: "1.15", letterSpacing: "0px" },
                description: { fontWeight: "normal", fontStyle: "normal", fontSize: "14px", lineHeight: "1.15", letterSpacing: "0px" },
              },
            },
            tags: [],
            isMandatory: false,
          };
        }

        // Parse choices if present
        const rawChoices = parseChoices(q.choices || q.Choices || '');

        // Determine question type
        const questionType = determineQuestionType(q.type || q.Type, rawChoices);

        let question = {
          id: `${questionType}-${Date.now() + index}`,
          type: questionType,
          question: `<p>${DOMPurify.sanitize(questionText)}</p>`,
          description: q.description || q.Description ? DOMPurify.sanitize(q.description || q.Description) : "",
          settings: {
            styles: {
              question: { fontWeight: "normal", fontStyle: "normal", fontSize: "14px", lineHeight: "1.15", letterSpacing: "0px" },
              description: { fontWeight: "normal", fontStyle: "normal", fontSize: "14px", lineHeight: "1.15", letterSpacing: "0px" },
            },
          },
          tags: [],
          isMandatory: false,
        };

        // Handle choices based on question type
        if (questionType === "SINGLE_CHOICE" || questionType === "MULTI_CHOICE" || questionType === "LIKERT_SCALE") {
          if (rawChoices.length > 0) {
            question.choices = rawChoices.map((text, idx) => ({
              text: DOMPurify.sanitize(text),
              value: text.toLowerCase().replace(/\s+/g, '_').replace(/[^\w]/g, ''),
              index: idx,
              tags: []
            }));
          } else {
            // Default choices if none provided
            if (questionType === "LIKERT_SCALE") {
              question.choices = [
                { text: intl.formatMessage({ id: "survey_builder.strongly_disagree" }), value: "strongly_disagree", index: 0, tags: [] },
                { text: intl.formatMessage({ id: "survey_builder.disagree" }), value: "disagree", index: 1, tags: [] },
                { text: intl.formatMessage({ id: "survey_builder.neutral" }), value: "neutral", index: 2, tags: [] },
                { text: intl.formatMessage({ id: "survey_builder.agree" }), value: "agree", index: 3, tags: [] },
                { text: intl.formatMessage({ id: "survey_builder.strongly_agree" }), value: "strongly_agree", index: 4, tags: [] },
              ];
            } else {
              question.choices = [
                { text: intl.formatMessage({ id: "survey_builder.option_1" }), value: "option_1", index: 0, tags: [] },
                { text: intl.formatMessage({ id: "survey_builder.option_2" }), value: "option_2", index: 1, tags: [] },
              ];
            }
          }
        }

        return question;
      });

      console.log("Processed CSV questions for confirmation:", normalizedQuestions);

      // Prepare import data for confirmation modal
      setCsvImportData({
        questions: normalizedQuestions,
        fileName: file.name,
        totalCount: normalizedQuestions.length,
        typeCounts: normalizedQuestions.reduce((acc, q) => {
          acc[q.type] = (acc[q.type] || 0) + 1;
          return acc;
        }, {})
      });
      setCsvConfirmationModalOpen(true);
      setAcceptedFiles([]);
    } catch (error) {
      console.error('Error processing CSV file:', error);
      enqueueSnackbar(intl.formatMessage({ id: "survey_builder.csv_import_failed" }, { error: error.message }), {
        variant: "error",
        autoHideDuration: 4000,
        anchorOrigin: { vertical: "bottom", horizontal: "right" },
      });
      setAcceptedFiles([]);
    } finally {
      processingRef.current = false;
    }
  };

  useEffect(() => {
    console.log("acceptedFiles changed:", acceptedFiles);
    if (acceptedFiles.length > 0 && !processingRef.current) {
      const file = acceptedFiles[0];
      if (steps.length > 0 || processedFileName) {
        setFileToProcess(file);
        setOpenClearConfirmationDialog(true);
        setAcceptedFiles([]);
      } else {
        setAcceptedFiles([]);
        processFile(file);
      }
    }
  }, [acceptedFiles]);

  // Reset scroll position when switching questions
  useEffect(() => {
    if (surveyAreaRef.current) {
      surveyAreaRef.current.scrollTop = 0;
      // Also reset any expanded content areas
      const expandedElements = surveyAreaRef.current.querySelectorAll('[data-expanded="true"]');
      expandedElements.forEach(element => {
        element.setAttribute('data-expanded', 'false');
      });
    }
  }, [currentStep]);

  // Sync local isPublic state with prop changes
  useEffect(() => {
    setIsPublic(settings.isPublic || false);
  }, [settings.isPublic]);

  // Initialize completion card background alpha from existing color
  useEffect(() => {
    if (settings.completionCardBackgroundColor && !settings.completionCardBackgroundColorAlpha) {
      const color = settings.completionCardBackgroundColor;
      if (color.startsWith('rgba')) {
        const match = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+),\s*([\d.]+)\)/);
        if (match && match[4]) {
          handleGlobalSettingsChange("completionCardBackgroundColorAlpha", parseFloat(match[4]));
        }
      } else {
        // If it's a hex or rgb color, set alpha to 0.95 (default for cards)
        handleGlobalSettingsChange("completionCardBackgroundColorAlpha", 0.95);
      }
    }
  }, [settings.completionCardBackgroundColor]);

  // Handle public survey toggle locally
  const handlePublicToggle = (checked) => {
    console.log("Public survey toggled to:", checked);
    setIsPublic(checked);
    // Also update the parent through the prop function
    handleGlobalSettingsChange("isPublic", checked);
  };

  // Color picker helper functions
  const openColorPicker = (event, currentColor, settingKey, onChange) => {
    setColorPickerState({
      open: true,
      anchorEl: event.currentTarget,
      currentColor: currentColor || "#ffffff",
      onChange: onChange,
      settingKey: settingKey
    });
  };

  const closeColorPicker = () => {
    setColorPickerState({
      open: false,
      anchorEl: null,
      currentColor: "#ffffff",
      onChange: null,
      settingKey: null
    });
  };

  const handleColorChange = (newColor) => {
    if (colorPickerState.onChange) {
      colorPickerState.onChange(newColor);
    }
    setColorPickerState(prev => ({
      ...prev,
      currentColor: newColor
    }));
  };

  const handleAccordionChange = (section) => (event, isExpanded) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: isExpanded
    }));
  };

  const handleConfirmClearAndUpload = () => {
    setOpenClearConfirmationDialog(false);
    const file = fileToProcess;
    setFileToProcess(null);
    if (file) {
      setAcceptedFiles([]);
      processFile(file);
    }
  };

  const handleCancelUpload = () => {
    setOpenClearConfirmationDialog(false);
    setFileToProcess(null);
    setAcceptedFiles([]);
  };

  const handleConfirmCsvImport = () => {
    if (csvImportData) {
      console.log("Confirming CSV import:", csvImportData.questions);
      setSteps(csvImportData.questions);
      setCurrentStep(0);
      setProcessedFileName(csvImportData.fileName);
      enqueueSnackbar(intl.formatMessage({ id: "survey_builder.csv_import_success" }, { count: csvImportData.totalCount }), {
        variant: "success",
        autoHideDuration: 3000,
        anchorOrigin: { vertical: "bottom", horizontal: "right" },
      });
      setCsvConfirmationModalOpen(false);
      setCsvImportData(null);
      setExpandedQuestions({});
    }
  };

  const handleCancelCsvImport = () => {
    setCsvConfirmationModalOpen(false);
    setCsvImportData(null);
    setExpandedQuestions({});
  };

  const toggleQuestionExpansion = (questionId) => {
    setExpandedQuestions(prev => ({
      ...prev,
      [questionId]: !prev[questionId]
    }));
  };

  const handleProceedWithUpload = () => {
    if (isDeleting) {
      console.log("Proceed with upload ignored: Deletion in progress");
      setAcceptedFiles([]);
      return;
    }
    const { questions, fileName } = pendingFile;
    console.log("Proceeding with file upload despite validation errors:", questions);
    setSteps(questions);
    setCurrentStep(0);
    setOpenValidationDialog(false);
    setInvalidQuestions([]);
    setPendingFile(null);
    setProcessedFileName(fileName);
    enqueueSnackbar(intl.formatMessage({ id: "survey.upload_success" }), {
      variant: "success",
      autoHideDuration: 2000,
      anchorOrigin: { vertical: "bottom", horizontal: "right" },
    });
    setAcceptedFiles([]);
  };

  const handlePreviousStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleNextStep = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleMoveStepUp = (index) => {
    if (index > 0) {
      setSteps(prevSteps => {
        const newSteps = [...prevSteps];
        [newSteps[index], newSteps[index - 1]] = [newSteps[index - 1], newSteps[index]];
        return newSteps;
      });
      // Adjust current step if necessary
      if (currentStep === index) {
        setCurrentStep(index - 1);
      } else if (currentStep === index - 1) {
        setCurrentStep(index);
      }
    }
  };

  const handleMoveStepDown = (index) => {
    if (index < steps.length - 1) {
      setSteps(prevSteps => {
        const newSteps = [...prevSteps];
        [newSteps[index], newSteps[index + 1]] = [newSteps[index + 1], newSteps[index]];
        return newSteps;
      });
      // Adjust current step if necessary
      if (currentStep === index) {
        setCurrentStep(index + 1);
      } else if (currentStep === index + 1) {
        setCurrentStep(index);
      }
    }
  };

  const handleDeleteClick = (index) => {
    setDeleteConfirmIndex(index);
  };

  const handleDeleteConfirm = (index) => {
    handleDeleteQuestion(index);
    setDeleteConfirmIndex(null);
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmIndex(null);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Render content based on active tab
  const renderTabContent = () => {
    switch (activeTab) {
      case 0: // Basic tab
        return (
          // A fixed-height column whose main area's panels scroll on their own.
          // It scrolls itself (rather than clipping) so anything stacked above
          // the main area can't push the builder out of reach.
          <Box data-testid="builder-basic-tab" sx={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            overflowX: "hidden",
            overflowY: "auto",
          }}>
            {/* Survey Header — the title on its own row, then the assessment
                settings strip (impact assessment / language) below it. */}
            <Box data-testid="builder-header" sx={{
              p: { xs: 2, md: 3 },
              borderBottom: "1px solid #e0e0e0",
              background: "linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)",
              display: "flex",
              flexDirection: "column",
              alignItems: "stretch",
              gap: 2,
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            }}>
              <Box sx={{
                flexGrow: 1,
                minWidth: 0,
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: { xs: "stretch", sm: "center" },
                gap: { xs: 1, sm: 2 },
              }}>
                <Typography variant="h6" sx={{
                  color: "#333",
                  fontWeight: "600",
                  whiteSpace: "nowrap"
                }}>
                  📋<FormattedMessage id="survey.title_label" />
                </Typography>
                <TextField
                  label=""
                  placeholder={intl.formatMessage({ id: "survey.title_placeholder" })}
                  value={settings.title || ""}
                  onChange={(e) => handleGlobalSettingsChange("title", e.target.value)}
                  fullWidth
                  variant="outlined"
                  size="small"
                  required
                  disable
                  inputProps={{ maxLength: 200 }}
                  sx={{
                    "& .MuiInputBase-root": {
                      backgroundColor: "white",
                      border: "3px solid rgb(139, 200, 219)",
                      borderRadius: "12px",
                      padding: "4px 4px",
                      boxShadow: "0 0 0 4px rgba(7, 122, 184, 0.1)",
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      animation: "pulse 2s infinite",
                      "&:hover": {
                        backgroundColor: "rgba(25, 220, 242, 0.05)",
                        borderColor: "#1976d2",
                        boxShadow: "0 0 0 6px rgba(4, 130, 213, 0.15), 0 4px 12px rgba(0,0,0,0.1)",
                        // transform: "translateY(-1px)",
                      },
                      "&.Mui-focused": {
                        backgroundColor: "white",
                        borderColor: "#1976d2",
                        boxShadow: "0 0 0 8px rgba(11, 91, 157, 0.2), 0 8px 20px rgba(0,0,0,0.15)",
                        transform: "translateY(-2px)",
                      }
                    },
                    "& .MuiInputBase-input": {
                      fontSize: "18px",
                      fontWeight: 600,
                      color: "#000",
                      "&::placeholder": {
                        color: "#aaa",
                        fontWeight: 400,
                      },
                    },
                    "& .MuiInputLabel-root": {
                      color: "#000",
                      fontWeight: 600,
                      fontSize: "16px",
                      "&.Mui-focused": {
                        color: "#1976d2",
                      }
                    },
                    "@keyframes pulse": {
                      "0%": {
                        boxShadow: "0 0 0 4px rgba(32, 106, 185, 0.1)",
                      },
                      "50%": {
                        boxShadow: "0 0 0 6px rgba(32, 106, 185, 0.15)",
                      },
                      "100%": {
                        boxShadow: "0 0 0 4px rgba(32, 106, 185, 0.1)",
                      }
                    },
                    "& .MuiInput-underline:before": {
                      borderBottom: "none !important",
                    },
                    "& .MuiInput-underline:after": {
                      borderBottom: "none !important",
                    },
                    "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                      borderBottom: "none !important",
                    },
                    "& .MuiInput-underline:focus:not(.Mui-disabled):before": {
                      borderBottom: "none !important",
                    },
                    "& .MuiInput-underline:focus:not(.Mui-disabled):after": {
                      borderBottom: "none !important",
                    },
                    "& .MuiOutlinedInput-root": {
                      outline: "none !important",
                      "& fieldset": {
                        border: "none !important",
                      },
                      "&:hover fieldset": {
                        border: "none !important",
                      },
                      "&.Mui-focused fieldset": {
                        border: "none !important",
                      }
                    }
                  }}
                />
              </Box>

              <SurveyProjectFields
                settings={settings}
                onChange={handleGlobalSettingsChange}
              />
            </Box>

            {/* Main Content Area */}
            <Box data-testid="builder-main-area" sx={{
              flexGrow: 1,
              display: "grid",
              gap: { xs: 2, md: 3 },
              gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "minmax(0, 1fr) 240px" },
              alignItems: "stretch",
              // Same horizontal padding as the header, so their edges line up.
              p: { xs: 2, md: 3 },
              [BUILDER_WIDE]: {
                gridTemplateColumns: "200px minmax(0, 1fr) 240px",
                gridTemplateRows: "minmax(0, 1fr)",
                minHeight: 420,
                overflow: "hidden",
              },
            }}>
              {/* Question Types Panel */}
              <Box data-testid="builder-palette" sx={{
                gridColumn: { md: "1 / -1" },
                display: "flex",
                flexDirection: "column",
                minWidth: 0,
                minHeight: 0,
                [BUILDER_WIDE]: { gridColumn: "auto" },
              }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: "medium", color: "#333" }}>
                    {intl.formatMessage({ id: "survey.survey_builder.add_question_title" })}
                  </Typography>
                </Box>

                {/* Conditional rendering based on screen size */}
                {isMobile ? (
                  // Mobile: Dropdown selection
                  <>
                    <Typography variant="body2" sx={{ mb: 2, color: "#666", fontWeight: "medium" }}>
                      {intl.formatMessage({ id: "survey.survey_builder.select_question_type_mobile" })}
                    </Typography>

                    <FormControl fullWidth sx={{ mb: 2 }}>
                      <InputLabel>{intl.formatMessage({ id: "survey.survey_builder.select_question_type_mobile" })}</InputLabel>
                      <Select
                        value=""
                        onChange={(e) => handleAddQuestionFromDropdown(e.target.value)}
                        label={intl.formatMessage({ id: "survey.survey_builder.select_question_type_mobile" })}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "8px",
                            backgroundColor: "white",
                          }
                        }}
                      >
                        {questionTypes.map((qt) => (
                          <MenuItem key={qt.id} value={qt.id}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <AddIcon sx={{ fontSize: 18, color: qt.style?.borderColor || "#1976d2" }} />
                              <Typography>{getQuestionTypeLabel(intl, qt)}</Typography>
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </>
                ) : (
                  // Desktop: Drag and drop interface
                  <>

                    <Box sx={{ flexGrow: 1, minHeight: 0, mb: 1.5, pt: 0.5, pb: 0.5, [BUILDER_WIDE]: { overflowY: "auto" } }}>
                      <Droppable droppableId="question-types" isDropDisabled={true}>
                        {(provided) => (
                          <Box
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                            sx={{
                              display: "grid",
                              gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
                              [BUILDER_WIDE]: { gridTemplateColumns: "1fr" },
                              gap: 1,
                            }}
                          >
                            {questionTypes.map((qt, index) => (
                              <Draggable key={qt.id} draggableId={qt.id} index={index}>
                                {(provided) => (
                                  <Card
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    {...provided.dragHandleProps}
                                    sx={{
                                      px: 2,
                                      py: 1.5,
                                      backgroundColor: qt.style?.backgroundColor || "#f8f9fa",
                                      cursor: "grab",
                                      border: `2px solid ${qt.style?.borderColor || "#e9ecef"}`,
                                      borderRadius: "8px",
                                      transition: "all 0.2s ease",
                                      "&:hover": {
                                        backgroundColor: qt.style?.backgroundColor ? `${qt.style.backgroundColor}dd` : "#e3f2fd",
                                        borderColor: qt.style?.borderColor || "#1976d2",
                                        transform: "translateY(-2px)",
                                        boxShadow: `0 4px 12px ${qt.style?.borderColor ? `rgba(${qt.style.borderColor === "#4caf50" ? "76, 175, 80" : "255, 152, 0"}, 0.25)` : "rgba(25, 118, 210, 0.15)"}`
                                      },
                                      "&:active": {
                                        transform: "translateY(0)",
                                      }
                                    }}
                                  >
                                    <Typography variant="body2" sx={{ fontWeight: "medium", textAlign: "center" }}>
                                      {getQuestionTypeLabel(intl, qt)}
                                    </Typography>
                                  </Card>
                                )}
                              </Draggable>
                            ))}
                            {provided.placeholder}
                          </Box>
                        )}
                      </Droppable>
                    </Box>

                    {/* Action Buttons Row */}
                    <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
                      <Tooltip title={intl.formatMessage({ id: "survey.survey_builder.bulk_add_questions_button" })}>
                        <Button
                          variant="contained"
                          onClick={handleOpenBulkAddDialog}
                          sx={{
                            flex: 1,
                            textTransform: "none",
                            backgroundColor: "#1976d2",
                            "&:hover": { backgroundColor: "#1565c0" },
                            borderRadius: "8px",
                            py: 1,
                            minWidth: "auto"
                          }}
                        >
                          <PlaylistAddIcon sx={{ fontSize: 18 }} />
                        </Button>
                      </Tooltip>

                      <Tooltip title={intl.formatMessage({ id: "survey.survey_builder.upload_questions_file_button" })}>
                        <Button
                          variant="outlined"
                          color="primary"
                          onClick={() => setCsvGuideModalOpen(true)}
                          sx={{
                            flex: 1,
                            textTransform: "none",
                            borderRadius: "8px",
                            py: 1,
                            borderColor: "#1976d2",
                            color: "#1976d2",
                            minWidth: "auto",
                            "&:hover": {
                              borderColor: "#1565c0",
                              backgroundColor: "rgba(25, 118, 210, 0.08)"
                            }
                          }}
                        >
                          <CloudUploadIcon sx={{ fontSize: 18 }} />
                        </Button>
                      </Tooltip>
                    </Box>
                  </>
                )}
              </Box>

              {/* Main Survey Builder Area */}
              <Box data-testid="builder-canvas" sx={{
                display: "flex",
                flexDirection: "column",
                overflow: "auto",
                minWidth: 0,
                minHeight: 420,
                [BUILDER_WIDE]: { minHeight: 0 },
              }}>
                <Box
                  ref={surveyAreaRef}
                  sx={{
                    flexGrow: 1,
                    position: "relative",
                    backgroundColor: "rgba(255, 255, 255, 0.95)",
                    borderRadius: "12px",
                    p: { xs: 1.5, sm: 3 },
                    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    overflow: "auto",
                  }}
                >



                  <Box sx={{ flexGrow: 1, overflow: "auto" }}>
                    <Droppable droppableId="survey-builder">
                      {(provided) => (
                        <Box
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                          sx={{
                            minHeight: "100%",
                            boxSizing: "border-box",
                            border: "2px dashed #1976d2",
                            borderRadius: "12px",
                            p: { xs: 1.5, sm: 4 },
                            backgroundColor: "rgba(25, 118, 210, 0.02)",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "center",
                            transition: "all 0.3s ease",
                            "&:hover": {
                              backgroundColor: "rgba(25, 118, 210, 0.04)",
                              borderColor: "#1565c0",
                            }
                          }}
                        >
                          {steps.length === 0 || !steps[currentStep] ? (
                            <Box sx={{ textAlign: "center", py: 4 }}>
                              <Typography variant="h5" sx={{ color: "#666", mb: 2, fontWeight: "medium" }}>
                                {intl.formatMessage({ id: "survey.survey_builder.drag_drop_message" })}
                              </Typography>
                            </Box>
                          ) : isDeleting ? (
                            <Box sx={{ textAlign: "center", py: 4 }}>
                              <Typography variant="h6" sx={{ color: "#d32f2f", fontWeight: "medium" }}>
                                {intl.formatMessage({ id: "survey.survey_builder.deleting_question_message" })}
                              </Typography>
                            </Box>
                          ) : (
                            <Box sx={{ width: "100%", maxWidth: "800px" }}>
                              <QuestionCard
                                question={steps[currentStep]}
                                index={currentStep}
                                handleQuestionChange={propHandleQuestionChange}
                                handleQuestionSettingsChange={handleQuestionSettingsChange}
                                handleQuestionStylesChange={handleQuestionStylesChange}
                                handleAddChoice={handleAddChoice}
                                handleChoiceChange={propHandleChoiceChange}
                                handleDeleteQuestion={handleDeleteQuestion}
                                handleDuplicateQuestion={handleDuplicateQuestion}
                                settings={settings}
                                isDeleting={isDeleting}
                                enqueueSnackbar={enqueueSnackbar}
                                segments={segments}
                                usedSegmentIds={usedSegmentIds}
                              />
                            </Box>
                          )}
                          {provided.placeholder}
                        </Box>
                      )}
                    </Droppable>
                  </Box>
                </Box>
              </Box>

              {/* Steps Overview Panel */}
              <Box data-testid="builder-steps" sx={{
                display: "flex",
                flexDirection: "column",
                minWidth: 0,
                minHeight: 300,
                maxHeight: 560,
                [BUILDER_WIDE]: { minHeight: 0, maxHeight: "none" },
              }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: "medium", color: "#333" }}>
                    {intl.formatMessage({ id: "survey.survey_builder.steps_title" })}
                  </Typography>
                  <Chip label={steps.length} size="small" color="primary" variant="filled" />
                </Box>

                <Box
                  sx={{
                    flexGrow: 1,
                    border: "1px solid #e0e0e0",
                    borderRadius: "12px",
                    backgroundColor: "#ffffff",
                    overflow: "hidden",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                  }}
                >
                  <Droppable droppableId="steps-sidebar">
                    {(provided, snapshot) => (
                      <Box
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        sx={{
                          height: "100%",
                          overflowY: "auto",
                          overflowX: "hidden",
                          backgroundColor: snapshot.isDraggingOver ? "rgba(25, 118, 210, 0.06)" : "transparent",
                          transition: "background-color 0.2s ease",
                          "&::-webkit-scrollbar": {
                            width: "6px",
                          },
                          "&::-webkit-scrollbar-track": {
                            backgroundColor: "#f1f1f1",
                            borderRadius: "3px",
                          },
                          "&::-webkit-scrollbar-thumb": {
                            backgroundColor: "#c1c1c1",
                            borderRadius: "3px",
                            "&:hover": {
                              backgroundColor: "#a8a8a8",
                            },
                          },
                        }}
                      >
                        {steps.length === 0 ? (
                          <Box
                            sx={{
                              minHeight: "100%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              p: 4,
                            }}
                          >
                            <Typography variant="body1" sx={{ textAlign: "center", color: "#999" }}>
                              {intl.formatMessage({ id: "survey.survey_builder.no_steps_message" })}
                            </Typography>
                          </Box>
                        ) : (
                          <>
                            {steps.map((step, index) => {
                              const getStepTypeLabel = (type) => {
                                switch (type) {
                                  case "PAGE_BREAK":
                                    return intl.formatMessage({ id: "survey.survey_builder.page_break_type" });
                                  case "SINGLE_CHOICE":
                                    return intl.formatMessage({ id: "survey.survey_builder.single_choice_type" });
                                  case "MULTIPLE_CHOICE":
                                    return intl.formatMessage({ id: "survey.survey_builder.multiple_choice_type" });
                                  case "LIKERT_SCALE":
                                    return intl.formatMessage({ id: "survey.survey_builder.likert_scale_type" });
                                  case "OPEN_TEXT":
                                    return intl.formatMessage({ id: "survey.survey_builder.open_text_type" });
                                  case "TEXT":
                                    return intl.formatMessage({ id: "survey.survey_builder.text_content_type" });
                                  default:
                                    return type.replace(/_/g, " ");
                                }
                              };

                              const isDeleting = deleteConfirmIndex === index;

                              return (
                                <Draggable key={step.id} draggableId={`step-${step.id}`} index={index}>
                                  {(dragProvided, dragSnapshot) => (
                                    <Box
                                      ref={dragProvided.innerRef}
                                      {...dragProvided.draggableProps}
                                      {...dragProvided.dragHandleProps}
                                      onClick={(e) => {
                                        if (e.target.closest('button')) return;
                                        setCurrentStep(index);
                                      }}
                                      sx={{
                                        p: 2,
                                        borderBottom: index < steps.length - 1 ? "1px solid #f0f0f0" : "none",
                                        borderLeft: currentStep === index ? "4px solid #1976d2" : "4px solid transparent",
                                        backgroundColor: isDeleting
                                          ? "#ffebee"
                                          : currentStep === index
                                            ? "#e3f2fd"
                                            : "transparent",
                                        cursor: isDeleting ? "default" : "grab",
                                        transition: "all 0.2s ease",
                                        ...(dragSnapshot.isDragging && {
                                          boxShadow: "0 8px 20px rgba(0,0,0,0.12)",
                                          backgroundColor: "#e3f2fd",
                                        }),
                                        "&:hover": {
                                          backgroundColor: isDeleting
                                            ? "#ffebee"
                                            : currentStep === index
                                              ? "#e3f2fd"
                                              : "#f8f9fa",
                                          transform: isDeleting ? "none" : "translateX(2px)",
                                        },
                                      }}
                                    >
                                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                        {isDeleting ? (
                                          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5, flex: 1 }}>
                                            <Typography variant="body2" sx={{ color: "#d32f2f", fontWeight: "500" }}>
                                              {intl.formatMessage({ id: "survey.survey_builder.confirm_delete" })}
                                            </Typography>
                                            <Box sx={{ display: "flex", gap: 1 }}>
                                              <Button
                                                size="small"
                                                variant="contained"
                                                color="error"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleDeleteConfirm(index);
                                                }}
                                                sx={{ minHeight: "24px", fontSize: "0.75rem", borderRadius: "6px" }}
                                              >
                                                {intl.formatMessage({ id: "general.delete" })}
                                              </Button>
                                              <Button
                                                size="small"
                                                variant="outlined"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleDeleteCancel();
                                                }}
                                                sx={{ minHeight: "24px", fontSize: "0.75rem", borderRadius: "6px" }}
                                              >
                                                {intl.formatMessage({ id: "general.cancel" })}
                                              </Button>
                                            </Box>
                                          </Box>
                                        ) : (
                                          <>
                                            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, flex: 1 }}>
                                              <Typography
                                                variant="body2"
                                                sx={{
                                                  fontWeight: currentStep === index ? "600" : "500",
                                                  color: currentStep === index ? "#1976d2" : "#333",
                                                  fontSize: "0.9rem"
                                                }}
                                              >
                                                {step.type === "PAGE_BREAK"
                                                  ? intl.formatMessage({ id: "survey.survey_builder.page_break_label" })
                                                  : intl.formatMessage({ id: "survey.survey_builder.step_label" }, { index: index + 1 })}
                                              </Typography>
                                              <Typography
                                                variant="caption"
                                                sx={{
                                                  color: currentStep === index ? "#1976d2" : "#666",
                                                  fontSize: "0.75rem",
                                                  textTransform: "capitalize",
                                                  fontWeight: "500"
                                                }}
                                              >
                                                {getStepTypeLabel(step.type)}
                                              </Typography>
                                              {step.question && step.question !== "<p></p>" && step.question !== "<p><br></p>" && (
                                                <Typography
                                                  variant="caption"
                                                  sx={{
                                                    color: "#666",
                                                    fontSize: "0.7rem",
                                                    lineHeight: 1.3,
                                                    display: "-webkit-box",
                                                    WebkitLineClamp: 2,
                                                    WebkitBoxOrient: "vertical",
                                                    overflow: "hidden",
                                                  }}
                                                >
                                                  {stripHtmlAndTruncate(step.question, 50)}
                                                </Typography>
                                              )}
                                            </Box>

                                            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                                              <Tooltip title={intl.formatMessage({ id: "survey.survey_builder.tooltip_move_up" })}>
                                                <span>
                                                  <IconButton
                                                    size="small"
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      handleMoveStepUp(index);
                                                    }}
                                                    disabled={index === 0 || isDeleting}
                                                    sx={{ p: 0.5, minWidth: "auto" }}
                                                  >
                                                    <KeyboardArrowUpIcon fontSize="small" />
                                                  </IconButton>
                                                </span>
                                              </Tooltip>
                                              <Tooltip title={intl.formatMessage({ id: "survey.survey_builder.tooltip_move_down" })}>
                                                <span>
                                                  <IconButton
                                                    size="small"
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      handleMoveStepDown(index);
                                                    }}
                                                    disabled={index === steps.length - 1 || isDeleting}
                                                    sx={{ p: 0.5, minWidth: "auto" }}
                                                  >
                                                    <KeyboardArrowDownIcon fontSize="small" />
                                                  </IconButton>
                                                </span>
                                              </Tooltip>
                                            </Box>

                                            <Tooltip title={intl.formatMessage({ id: "survey.survey_builder.tooltip_delete" })}>
                                              <IconButton
                                                size="small"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleDeleteClick(index);
                                                }}
                                                sx={{
                                                  p: 0.5,
                                                  minWidth: "auto",
                                                  color: "#d32f2f",
                                                  "&:hover": { backgroundColor: "#ffebee" }
                                                }}
                                              >
                                                <DeleteIcon fontSize="small" />
                                              </IconButton>
                                            </Tooltip>
                                          </>
                                        )}
                                      </Box>
                                    </Box>
                                  )}
                                </Draggable>
                              );
                            })}
                            {provided.placeholder}
                          </>
                        )}
                      </Box>
                    )}
                  </Droppable>
                </Box>
              </Box>
            </Box>
          </Box>
        );

      case 1: // Advanced tab
        return (
          <Box sx={{
            p: 4,
            height: "100%",
          }}>

            {/* Survey Behavior */}
            <Accordion
              expanded={expandedSections.behavior}
              onChange={handleAccordionChange('behavior')}
              sx={{
                mb: 3,
                borderRadius: "16px !important",
                background: "linear-gradient(135deg, #ffffff 0%, #fafbfc 100%)",
                boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
                border: "1px solid rgba(237, 108, 2, 0.08)",
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: "0 12px 40px rgba(0,0,0,0.15)",
                },
                "&.Mui-expanded": {
                  margin: "0 0 24px 0",
                },
                "&:before": {
                  display: "none",
                }
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: "rgba(133, 63, 6, 0.3)" }} />}
                sx={{
                  borderRadius: "16px",
                  minHeight: "64px",
                  "&.Mui-expanded": {
                    borderRadius: "16px 16px 0 0",
                    minHeight: "64px",
                  },
                  "& .MuiAccordionSummary-content": {
                    margin: "16px 0",
                    "&.Mui-expanded": {
                      margin: "16px 0",
                    }
                  }
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, width: "100%" }}>
                  <Typography variant="h6" sx={{
                    color: "#333",
                    fontWeight: "600",
                    fontSize: "1.2rem"
                  }}>
                    <FormattedMessage id="survey_builder.settings" />
                  </Typography>
                  <Box sx={{
                    flexGrow: 1,
                    height: "2px",
                    background: "linear-gradient(90deg, rgba(69, 33, 4, 0.6) 0%, rgba(133, 63, 6, 0.3) 100%)",
                    borderRadius: "1px",
                    mr: 2
                  }} />
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0, pb: 4, px: 4 }}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                  {/* Checkboxes in one row */}
                  <Box sx={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={settings.showQuestionNumber || false}
                          onChange={(e) => handleGlobalSettingsChange("showQuestionNumber", e.target.checked)}
                          sx={{
                            color: "#ed6c02",
                            "&.Mui-checked": {
                              color: "#ed6c02",
                            },
                            transform: "scale(1.1)",
                          }}
                        />
                      }
                      label={
                        <Typography variant="body1" sx={{ color: "#333", fontWeight: "500", fontSize: "0.95rem" }}>
                          <FormattedMessage id="survey_builder.show_question_numbers" />
                        </Typography>
                      }
                      sx={{ margin: 0 }}
                    />

                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={settings.showProgressBar || false}
                          onChange={(e) => handleGlobalSettingsChange("showProgressBar", e.target.checked)}
                          sx={{
                            color: "#ed6c02",
                            "&.Mui-checked": {
                              color: "#ed6c02",
                            },
                            transform: "scale(1.1)",
                          }}
                        />
                      }
                      label={
                        <Typography variant="body1" sx={{ color: "#333", fontWeight: "500", fontSize: "0.95rem" }}>
                          <FormattedMessage id="survey_builder.show_progress_bar" />
                        </Typography>
                      }
                      sx={{ margin: 0 }}
                    />
                  </Box>

                  <TextField
                    label={intl.formatMessage({ id: "survey_builder.survey_description" })}
                    placeholder={intl.formatMessage({ id: "survey_builder.survey_description_placeholder" })}
                    value={settings.description || ""}
                    onChange={(e) => handleGlobalSettingsChange("description", e.target.value)}
                    fullWidth
                    multiline
                    minRows={2}
                    maxRows={6}
                    variant="outlined"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "12px",
                        backgroundColor: "white",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          backgroundColor: "white",
                          boxShadow: "0 4px 12px rgba(237, 108, 2, 0.1)",
                        },
                        "&.Mui-focused": {
                          backgroundColor: "white",
                          boxShadow: "0 0 0 3px rgba(237, 108, 2, 0.15)",
                        }
                      },
                      "& .MuiInputBase-input": {
                        resize: "vertical",
                        minHeight: "80px",
                      },
                      "& .MuiInputLabel-root": {
                        color: "#666",
                        "&.Mui-focused": {
                          color: "#ed6c02",
                        }
                      }
                    }}
                  />

                  <TextField
                    label={intl.formatMessage({ id: "survey_builder.redirect_link" })}
                    value={settings.redirectLink || ""}
                    onChange={(e) => handleGlobalSettingsChange("redirectLink", e.target.value)}
                    fullWidth
                    variant="outlined"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "12px",
                        backgroundColor: "white",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          backgroundColor: "white",
                          boxShadow: "0 4px 12px rgba(237, 108, 2, 0.1)",
                        },
                        "&.Mui-focused": {
                          backgroundColor: "white",
                          boxShadow: "0 0 0 3px rgba(237, 108, 2, 0.15)",
                        }
                      }
                    }}
                  />
                </Box>
              </AccordionDetails>
            </Accordion>

            {/* Completion/Submission Styling */}
            <Accordion
              expanded={expandedSections.completion}
              onChange={handleAccordionChange('completion')}
              sx={{
                mb: 3,
                borderRadius: "16px !important",
                background: "linear-gradient(135deg, #ffffff 0%, #fafbfc 100%)",
                boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
                border: "1px solid rgba(76, 175, 80, 0.08)",
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: "0 12px 40px rgba(0,0,0,0.15)",
                },
                "&.Mui-expanded": {
                  margin: "0 0 24px 0",
                },
                "&:before": {
                  display: "none",
                }
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: "#4caf50" }} />}
                sx={{
                  borderRadius: "16px",
                  minHeight: "64px",
                  "&.Mui-expanded": {
                    borderRadius: "16px 16px 0 0",
                    minHeight: "64px",
                  },
                  "& .MuiAccordionSummary-content": {
                    margin: "16px 0",
                    "&.Mui-expanded": {
                      margin: "16px 0",
                    }
                  }
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, width: "100%" }}>
                  <Typography variant="h6" sx={{
                    color: "rgb(76, 175, 80)",
                    fontWeight: "600",
                    fontSize: "1.2rem"
                  }}>
                    <FormattedMessage id="survey_builder.submission_page" />
                  </Typography>
                  <Box sx={{
                    flexGrow: 1,
                    height: "2px",
                    background: "linear-gradient(90deg, rgba(76, 175, 80, 0.3) 0%, rgba(76, 175, 80, 0.6) 100%)",
                    borderRadius: "1px",
                    mr: 2
                  }} />
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0, pb: 4, px: 4 }}>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                  {/* Completion Page Colors */}
                  <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap" }}>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, minWidth: "300px" }}>
                      <Typography variant="body2" sx={{ color: "#666", fontSize: "0.8rem" }}>
                        <FormattedMessage id="survey_builder.completion_background_gradient" />
                      </Typography>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
                          <Box
                            sx={{
                              width: "50px",
                              height: "35px",
                              backgroundColor: settings.completionBackgroundGradient?.start || DEFAULT_GRADIENT.start,
                              border: "2px solid #e0e0e0",
                              borderRadius: "6px",
                              cursor: "pointer",
                              transition: "all 0.2s ease",
                              "&:hover": {
                                transform: "scale(1.05)",
                                boxShadow: "0 3px 8px rgba(0,0,0,0.2)"
                              }
                            }}
                            onClick={(event) => openColorPicker(
                              event,
                              settings.completionBackgroundGradient?.start || DEFAULT_GRADIENT.start,
                              "completionBackgroundGradientStart",
                              (color) => handleGlobalSettingsChange("completionBackgroundGradient", {
                                ...settings.completionBackgroundGradient,
                                start: color
                              })
                            )}
                          />
                        </Box>
                        <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column", alignItems: "center" }}>
                          <Box
                            sx={{
                              width: "100%",
                              height: "35px",
                              background: `linear-gradient(135deg, ${settings.completionBackgroundGradient?.start || DEFAULT_GRADIENT.start}, ${settings.completionBackgroundGradient?.end || DEFAULT_GRADIENT.end})`,
                              border: "1px solid #e0e0e0",
                              borderRadius: "6px"
                            }}
                          />
                        </Box>
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
                          <Box
                            sx={{
                              width: "50px",
                              height: "35px",
                              backgroundColor: settings.completionBackgroundGradient?.end || DEFAULT_GRADIENT.end,
                              border: "2px solid #e0e0e0",
                              borderRadius: "6px",
                              cursor: "pointer",
                              transition: "all 0.2s ease",
                              "&:hover": {
                                transform: "scale(1.05)",
                                boxShadow: "0 3px 8px rgba(0,0,0,0.2)"
                              }
                            }}
                            onClick={(event) => openColorPicker(
                              event,
                              settings.completionBackgroundGradient?.end || DEFAULT_GRADIENT.end,
                              "completionBackgroundGradientEnd",
                              (color) => handleGlobalSettingsChange("completionBackgroundGradient", {
                                ...settings.completionBackgroundGradient,
                                end: color
                              })
                            )}
                          />
                        </Box>
                      </Box>
                    </Box>

                    <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 3 }}>
                      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1, minWidth: "120px" }}>
                        <Typography variant="body2" sx={{ color: "#666", fontSize: "0.8rem" }}>
                          <FormattedMessage id="survey_builder.completion_text_color" />
                        </Typography>
                        <Box
                          sx={{
                            width: "50px",
                            height: "35px",
                            backgroundColor: settings.completionTextColor || "#2e7d32",
                            border: "2px solid #e0e0e0",
                            borderRadius: "6px",
                            cursor: "pointer",
                            transition: "all 0.2s ease",
                            "&:hover": {
                              transform: "scale(1.05)",
                              boxShadow: "0 3px 8px rgba(0,0,0,0.2)"
                            }
                          }}
                          onClick={(event) => openColorPicker(
                            event,
                            settings.completionTextColor || "#2e7d32",
                            "completionTextColor",
                            (color) => handleGlobalSettingsChange("completionTextColor", color)
                          )}
                        />
                      </Box>

                      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, minWidth: "180px" }}>
                        <Typography variant="body2" sx={{ color: "#666", fontSize: "0.8rem" }}>
                          <FormattedMessage id="survey_builder.completion_card_background" />
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Box
                            sx={{
                              width: "50px",
                              height: "35px",
                              backgroundColor: settings.completionCardBackgroundColor || "rgba(255, 255, 255, 0.95)",
                              border: "2px solid #e0e0e0",
                              borderRadius: "6px",
                              cursor: "pointer",
                              transition: "all 0.2s ease",
                              "&:hover": {
                                transform: "scale(1.05)",
                                boxShadow: "0 3px 8px rgba(0,0,0,0.2)"
                              }
                            }}
                            onClick={(event) => {
                              // Extract hex part if it's an rgba color
                              const hexValue = settings.completionCardBackgroundColor?.startsWith('rgba') || settings.completionCardBackgroundColor?.startsWith('rgb')
                                ? "#ffffff" // Default if rgba
                                : settings.completionCardBackgroundColor || "#ffffff";

                              openColorPicker(
                                event,
                                hexValue,
                                "completionCardBackgroundColor",
                                (color) => {
                                  // Keep existing alpha if it was rgba, otherwise use 0.95 opacity
                                  const currentAlpha = settings.completionCardBackgroundColorAlpha || 0.95;
                                  const r = parseInt(color.slice(1, 3), 16);
                                  const g = parseInt(color.slice(3, 5), 16);
                                  const b = parseInt(color.slice(5, 7), 16);
                                  const rgbaColor = `rgba(${r}, ${g}, ${b}, ${currentAlpha})`;
                                  handleGlobalSettingsChange("completionCardBackgroundColor", rgbaColor);
                                }
                              );
                            }}
                          />
                          <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "row", alignItems: "center" }}>
                            <Typography variant="caption" sx={{ color: "#666", fontSize: "0.7rem", mb: 0.5, mr: 1, display: "block" }}>
                              <FormattedMessage id="survey_builder.opacity" />
                            </Typography>
                            <Slider
                              value={settings.completionCardBackgroundColorAlpha || 0.95}
                              onChange={(e, value) => {
                                handleGlobalSettingsChange("completionCardBackgroundColorAlpha", value);
                                // Update the color with new alpha
                                const currentColor = settings.completionCardBackgroundColor || "#ffffff";
                                if (currentColor.startsWith('#')) {
                                  const hex = currentColor;
                                  const r = parseInt(hex.slice(1, 3), 16);
                                  const g = parseInt(hex.slice(3, 5), 16);
                                  const b = parseInt(hex.slice(5, 7), 16);
                                  const rgbaColor = `rgba(${r}, ${g}, ${b}, ${value})`;
                                  handleGlobalSettingsChange("completionCardBackgroundColor", rgbaColor);
                                } else if (currentColor.startsWith('rgba')) {
                                  const match = currentColor.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*[\d.]+)?\)/);
                                  if (match) {
                                    const rgbaColor = `rgba(${match[1]}, ${match[2]}, ${match[3]}, ${value})`;
                                    handleGlobalSettingsChange("completionCardBackgroundColor", rgbaColor);
                                  }
                                }
                              }}
                              min={0}
                              max={1}
                              step={0.05}
                              size="small"
                              sx={{
                                "& .MuiSlider-thumb": {
                                  backgroundColor: "#4caf50",
                                  width: 16,
                                  height: 16
                                },
                                "& .MuiSlider-track": {
                                  backgroundColor: "#4caf50",
                                  height: 4
                                },
                                "& .MuiSlider-rail": {
                                  backgroundColor: "#e0e0e0",
                                  height: 4
                                }
                              }}
                            />
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </Box>

                  {/* Completion Message Styling */}
                  <TextField
                    label={intl.formatMessage({ id: "survey_builder.success_message_title" })}
                    value={settings.successTitle || intl.formatMessage({ id: "survey_builder.thank_you" })}
                    onChange={(e) => handleGlobalSettingsChange("successTitle", e.target.value)}
                    fullWidth
                    variant="outlined"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "12px",
                        backgroundColor: "white",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          backgroundColor: "white",
                          boxShadow: "0 4px 12px rgba(76, 175, 80, 0.1)",
                        },
                        "&.Mui-focused": {
                          backgroundColor: "white",
                          boxShadow: "0 0 0 3px rgba(76, 175, 80, 0.15)",
                        }
                      }
                    }}
                  />

                  <TextField
                    label={intl.formatMessage({ id: "survey_builder.success_message" })}
                    value={settings.successDescription || intl.formatMessage({ id: "survey_builder.success_message_default" })}
                    onChange={(e) => handleGlobalSettingsChange("successDescription", e.target.value)}
                    fullWidth
                    multiline
                    rows={2}
                    variant="outlined"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "12px",
                        backgroundColor: "white",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          backgroundColor: "white",
                          boxShadow: "0 4px 12px rgba(76, 175, 80, 0.1)",
                        },
                        "&.Mui-focused": {
                          backgroundColor: "white",
                          boxShadow: "0 0 0 3px rgba(76, 175, 80, 0.15)",
                        }
                      }
                    }}
                  />

                  <TextField
                    label={intl.formatMessage({ id: "survey_builder.success_message_description" })}
                    value={settings.endMessage || ""}
                    onChange={(e) => handleGlobalSettingsChange("endMessage", e.target.value)}
                    fullWidth
                    multiline
                    rows={2}
                    variant="outlined"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "12px",
                        backgroundColor: "white",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          backgroundColor: "white",
                          boxShadow: "0 4px 12px rgba(237, 108, 2, 0.1)",
                        },
                        "&.Mui-focused": {
                          backgroundColor: "white",
                          boxShadow: "0 0 0 3px rgba(237, 108, 2, 0.15)",
                        }
                      }
                    }}
                  />
                </Box>
              </AccordionDetails>
            </Accordion>

            {/* Background Section */}
            <Accordion
              expanded={expandedSections.background}
              onChange={handleAccordionChange('background')}
              sx={{
                mb: 3,
                borderRadius: "16px !important",
                background: "linear-gradient(135deg, #ffffff 0%, #fafbfc 100%)",
                boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
                border: "1px solid rgba(237, 108, 2, 0.08)",
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: "0 12px 40px rgba(0,0,0,0.15)",
                },
                "&.Mui-expanded": {
                  margin: "0 0 24px 0",
                },
                "&:before": {
                  display: "none",
                }
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: "#ed6c02" }} />}
                sx={{
                  borderRadius: "16px",
                  minHeight: "64px",
                  "&.Mui-expanded": {
                    borderRadius: "16px 16px 0 0",
                    minHeight: "64px",
                  },
                  "& .MuiAccordionSummary-content": {
                    margin: "16px 0",
                    "&.Mui-expanded": {
                      margin: "16px 0",
                    }
                  }
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, width: "100%" }}>
                  <Typography variant="h6" sx={{
                    color: "rgba(237, 108, 2, 0.6)",
                    fontWeight: "600",
                    fontSize: "1.2rem"
                  }}>
                    <FormattedMessage id="survey_builder.appearance" />
                  </Typography>
                  <Box sx={{
                    flexGrow: 1,
                    height: "2px",
                    background: "linear-gradient(90deg, rgba(237, 108, 2, 0.3) 0%, rgba(237, 108, 2, 0.6) 100%)",
                    borderRadius: "1px",
                    mr: 2
                  }} />
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0, pb: 2, px: 4 }}>

                {/* Logo Upload and Alignment in one row */}
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", mb: 3 }}>
                  <Typography variant="body1" sx={{ mb: 1, fontWeight: "500", color: "rgb(237, 108, 2)" }}>
                    <FormattedMessage id="survey_builder.logo" />
                  </Typography>
                  {/* Logo Upload/Preview */}
                  <Box sx={{ width: "100%", display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "space-between" }}>
                    <Box sx={{ flexGrow: 1 }}>
                      {!settings.logo ? (
                        <Button
                          variant="outlined"
                          component="label"
                          sx={{
                            textTransform: "none",
                            borderRadius: "12px",
                            py: 1.5,
                            border: "2px dashed rgba(25, 118, 210, 0.3)",
                            "&:hover": {
                              borderColor: "#1976d2",
                              backgroundColor: "rgba(25, 118, 210, 0.05)"
                            }
                          }}
                        >
                          <FormattedMessage id="survey_builder.upload_logo" />
                          <input
                            type="file"
                            accept="image/*"
                            hidden
                            onChange={(e) => {
                              const file = e.target.files[0];
                              if (file) {
                                const reader = new FileReader();
                                reader.onloadend = () => {
                                  console.log("Logo uploaded:", reader.result);
                                  handleGlobalSettingsChange("logo", reader.result);
                                };
                                reader.readAsDataURL(file);
                              }
                            }}
                          />
                        </Button>
                      ) : (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Box sx={{
                            display: "inline-block",
                            p: 1,
                            border: "1px solid #e0e0e0",
                            borderRadius: "8px",
                            backgroundColor: "#f8f9fa"
                          }}>
                            <img
                              src={settings.logo}
                              alt="Logo"
                              style={{ maxWidth: "80px", maxHeight: "40px", objectFit: "contain" }}
                            />
                          </Box>
                          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                            <Typography variant="body2" sx={{ color: "#666", fontSize: "0.8rem" }}>
                              <FormattedMessage id="survey_builder.logo_uploaded" />
                            </Typography>
                            <Button
                              onClick={() => handleGlobalSettingsChange("logo", null)}
                              color="error"
                              size="small"
                              sx={{ fontSize: "0.75rem", minWidth: "auto", p: "2px 8px" }}
                            >
                              <FormattedMessage id="survey_builder.remove" />
                            </Button>
                          </Box>
                        </Box>
                      )}
                    </Box>

                    {/* Logo Alignment */}
                    <FormControl sx={{ minWidth: "140px" }}>
                      <InputLabel><FormattedMessage id="survey_builder.logo_alignment" /></InputLabel>
                      <Select
                        value={settings.logoAlignment || "Left"}
                        onChange={(e) => handleGlobalSettingsChange("logoAlignment", e.target.value)}
                        label={intl.formatMessage({ id: "survey_builder.logo_alignment" })}
                        size="small"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "12px",
                            backgroundColor: "white",
                            transition: "all 0.2s ease",
                            "&:hover": {
                              backgroundColor: "white",
                              boxShadow: "0 4px 12px rgba(25, 118, 210, 0.1)",
                            },
                            "&.Mui-focused": {
                              backgroundColor: "white",
                              boxShadow: "0 0 0 3px rgba(25, 118, 210, 0.15)",
                            }
                          }
                        }}
                      >
                        <MenuItem value="Left"><FormattedMessage id="survey_builder.left" /></MenuItem>
                        <MenuItem value="Center"><FormattedMessage id="survey_builder.center" /></MenuItem>
                        <MenuItem value="Right"><FormattedMessage id="survey_builder.right" /></MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                </Box>

                <Divider sx={{
                  mb: 2,
                  borderColor: "rgba(237, 108, 2, 0.05)",
                  borderWidth: "1px",
                }} />

                {/* Background Mode Selection */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body1" sx={{ mb: 1, fontWeight: "500", color: "rgb(237, 108, 2)" }}>
                    <FormattedMessage id="survey_builder.background" />
                  </Typography>
                  <Box sx={{ display: "flex", gap: { xs: 3, md: 1 }, flexDirection: { xs: "column", md: "row" }, justifyContent: "space-between", alignItems: "center" }}>
                    <Box>
                      {["single", "gradient", "image"].map((mode) => (
                        <Button
                          key={mode}
                          variant={settings.backgroundMode === mode ? "contained" : "outlined"}
                          onClick={() => handleGlobalSettingsChange("backgroundMode", mode)}
                          sx={{
                            textTransform: "none",
                            borderRadius: "12px",
                            px: 2,
                            py: 1,
                            mr: 1,
                            minWidth: "100px",
                            "&.Mui-contained": {
                              background: "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)",
                              boxShadow: "0 4px 12px rgba(25, 118, 210, 0.3)"
                            }
                          }}
                        >
                          {mode === "single" ? <FormattedMessage id="survey_builder.solid" /> : mode === "gradient" ? <FormattedMessage id="survey_builder.gradient" /> : <FormattedMessage id="survey_builder.image" />}
                        </Button>
                      ))}

                    </Box>
                    <Box>
                      {/* Background Settings based on mode */}
                      {settings.backgroundMode === "single" && (
                        <Box sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2
                        }}>
                          <Box
                            sx={{
                              width: "60px",
                              height: "40px",
                              backgroundColor: settings.backgroundColor || "#ffffff",
                              border: "2px solid #e0e0e0",
                              borderRadius: "8px",
                              cursor: "pointer",
                              transition: "all 0.2s ease",
                              "&:hover": {
                                transform: "scale(1.05)",
                                boxShadow: "0 4px 12px rgba(0,0,0,0.2)"
                              }
                            }}
                            onClick={(event) => openColorPicker(
                              event,
                              settings.backgroundColor || "#ffffff",
                              "backgroundColor",
                              (color) => handleGlobalSettingsChange("backgroundColor", color)
                            )}
                          />
                          <Typography variant="body2" sx={{ color: "#666" }}>
                            <FormattedMessage id="survey_builder.click_to_change_color" />
                          </Typography>
                        </Box>
                      )}

                      {settings.backgroundMode === "gradient" && (
                        <Box>
                          <Box sx={{ display: "flex", gap: 0, alignItems: "center" }}>
                            <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 1 }}>
                              <Typography variant="caption"><FormattedMessage id="survey_builder.start" /></Typography>
                              <Box
                                sx={{
                                  width: "60px",
                                  height: "40px",
                                  backgroundColor: settings.surveyColour?.start || DEFAULT_GRADIENT.start,
                                  border: "2px solid #e0e0e0",
                                  borderRadius: "8px",
                                  cursor: "pointer",
                                  transition: "all 0.2s ease",
                                  "&:hover": {
                                    transform: "scale(1.05)",
                                    boxShadow: "0 4px 12px rgba(0,0,0,0.2)"
                                  }
                                }}
                                onClick={(event) => openColorPicker(
                                  event,
                                  settings.surveyColour?.start || DEFAULT_GRADIENT.start,
                                  "surveyColourStart",
                                  (color) => handleGlobalSettingsChange("surveyColour", {
                                    ...settings.surveyColour,
                                    start: color
                                  })
                                )}
                              />
                            </Box>
                            <Box sx={{ flexGrow: 1 }}>
                              <Box
                                sx={{
                                  width: "100px",
                                  height: "40px",
                                  background: `linear-gradient(to right, ${settings.surveyColour?.start || DEFAULT_GRADIENT.start}, ${settings.surveyColour?.end || DEFAULT_GRADIENT.end})`,
                                  border: "1px solid #e0e0e0",
                                  borderRadius: "4px"
                                }}
                              />
                            </Box>
                            <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 1 }}>
                              <Box
                                sx={{
                                  width: "60px",
                                  height: "40px",
                                  backgroundColor: settings.surveyColour?.end || DEFAULT_GRADIENT.end,
                                  border: "2px solid #e0e0e0",
                                  borderRadius: "8px",
                                  cursor: "pointer",
                                  transition: "all 0.2s ease",
                                  "&:hover": {
                                    transform: "scale(1.05)",
                                    boxShadow: "0 4px 12px rgba(0,0,0,0.2)"
                                  }
                                }}
                                onClick={(event) => openColorPicker(
                                  event,
                                  settings.surveyColour?.end || DEFAULT_GRADIENT.end,
                                  "surveyColourEnd",
                                  (color) => handleGlobalSettingsChange("surveyColour", {
                                    ...settings.surveyColour,
                                    end: color
                                  })
                                )}
                              />
                              <Typography variant="caption"><FormattedMessage id="survey_builder.end" /></Typography>
                            </Box>
                          </Box>
                        </Box>
                      )}

                      {settings.backgroundMode === "image" && (
                        <Box>
                          {!settings.backgroundImage && (
                            <Button
                              variant="outlined"
                              component="label"
                              sx={{
                                textTransform: "none",
                                borderRadius: "12px",
                                py: 1.5,
                                border: "2px dashed rgba(237, 108, 2, 0.3)",
                                "&:hover": {
                                  borderColor: "#ed6c02",
                                  backgroundColor: "rgba(237, 108, 2, 0.05)"
                                }
                              }}
                            >
                              <FormattedMessage id="survey_builder.upload_background_image" />
                              <input
                                type="file"
                                accept="image/*"
                                hidden
                                onChange={(e) => {
                                  const file = e.target.files[0];
                                  if (file) {
                                    const reader = new FileReader();
                                    reader.onloadend = () => {
                                      console.log("Background image uploaded:", reader.result);
                                      handleGlobalSettingsChange("backgroundImage", reader.result);
                                    };
                                    reader.readAsDataURL(file);
                                  }
                                }}
                              />
                            </Button>
                          )}

                          {settings.backgroundImage && (
                            <Box sx={{ display: "flex", flexDirection: "row", gap: 2, alignItems: "center" }}>
                              {/* Brightness Control */}
                              <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexGrow: 1 }}>
                                <Typography variant="body2" sx={{ fontSize: "0.8rem", minWidth: "70px" }}>
                                  <FormattedMessage id="survey_builder.brightness" />
                                </Typography>
                                <Slider
                                  value={settings.backgroundBrightness || 100}
                                  onChange={(e, value) => handleGlobalSettingsChange("backgroundBrightness", value)}
                                  min={0}
                                  max={100}
                                  step={1}
                                  valueLabelDisplay="auto"
                                  sx={{
                                    width: "100px",
                                    flexGrow: 1,
                                    "& .MuiSlider-thumb": {
                                      backgroundColor: "#ed6c02"
                                    },
                                    "& .MuiSlider-track": {
                                      backgroundColor: "#ed6c02"
                                    },
                                    "& .MuiSlider-rail": {
                                      backgroundColor: "#e0e0e0"
                                    }
                                  }}
                                />
                              </Box>
                              {/* Image Preview */}
                              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                <Box
                                  sx={{
                                    position: "relative",
                                    borderRadius: "12px",
                                    overflow: "hidden",
                                    border: "2px solid #e0e0e0",
                                    cursor: "pointer",
                                    transition: "all 0.2s ease",
                                    "&:hover": {
                                      transform: "scale(1.02)",
                                      boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
                                      borderColor: "#ed6c02"
                                    }
                                  }}
                                  onClick={() => setImageModalOpen(true)}
                                >
                                  <img
                                    src={settings.backgroundImage}
                                    alt="Background Preview"
                                    style={{
                                      width: "120px",
                                      height: "80px",
                                      objectFit: "cover",
                                      display: "block"
                                    }}
                                  />
                                  <Box
                                    sx={{
                                      position: "absolute",
                                      top: 0,
                                      left: 0,
                                      right: 0,
                                      bottom: 0,
                                      background: "rgba(0,0,0,0.5)",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      opacity: 0,
                                      transition: "opacity 0.2s ease",
                                      "&:hover": {
                                        opacity: 1
                                      }
                                    }}
                                  >
                                    <Typography variant="caption" sx={{ color: "white", fontWeight: "bold" }}>
                                      <FormattedMessage id="survey_builder.click_to_view" />
                                    </Typography>
                                  </Box>
                                </Box>

                                <IconButton
                                  onClick={() => handleGlobalSettingsChange("backgroundImage", null)}
                                  color="error"
                                  size="small"
                                  sx={{
                                    backgroundColor: "rgba(244, 67, 54, 0.1)",
                                    "&:hover": {
                                      backgroundColor: "rgba(244, 67, 54, 0.2)"
                                    }
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            </Box>
                          )}
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Box>

                <Divider sx={{
                  mb: 2,
                  borderColor: "rgba(237, 108, 2, 0.05)",
                  borderWidth: "1px",
                }} />

                <Box sx={{ mb: 3 }}>
                  <Typography variant="body1" sx={{ mb: 2, fontWeight: "500", color: "rgb(237, 108, 2)" }}>
                    <FormattedMessage id="survey_builder.buttons" />
                  </Typography>
                  {/* Button Texts with Color Pickers */}
                  <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "space-evenly", flexWrap: "wrap", gap: 3 }}>
                    {/* Back Button */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <TextField
                        label={intl.formatMessage({ id: "survey_builder.back_button" })}
                        value={settings.backButtonText || intl.formatMessage({ id: "survey_builder.previous" })}
                        onChange={(e) => handleGlobalSettingsChange("backButtonText", e.target.value)}
                        variant="outlined"
                        size="small"
                        sx={{
                          width: "100px",
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "8px",
                            backgroundColor: "white",
                            transition: "all 0.2s ease",
                            "&:hover": {
                              backgroundColor: "white",
                              boxShadow: "0 2px 6px rgba(237, 108, 2, 0.1)",
                            },
                            "&.Mui-focused": {
                              backgroundColor: "white",
                              boxShadow: "0 0 0 2px rgba(237, 108, 2, 0.15)",
                            }
                          },
                          "& .MuiInputLabel-root": {
                            fontSize: "0.8rem"
                          }
                        }}
                      />
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <Box
                          sx={{
                            width: "30px",
                            height: "30px",
                            backgroundColor: settings.backButtonBackgroundColor || "rgba(255,255,255,0.9)",
                            border: "2px solid #e0e0e0",
                            borderRadius: "4px",
                            cursor: "pointer",
                            transition: "all 0.2s ease",
                            "&:hover": {
                              transform: "scale(1.05)",
                              boxShadow: "0 2px 6px rgba(0,0,0,0.2)"
                            }
                          }}
                          onClick={(event) => openColorPicker(
                            event,
                            settings.backButtonBackgroundColor || "#ffffff",
                            "backButtonBackgroundColor",
                            (color) => handleGlobalSettingsChange("backButtonBackgroundColor", color)
                          )}
                          title="Background Color"
                        />
                        <Box
                          sx={{
                            width: "30px",
                            height: "30px",
                            backgroundColor: settings.backButtonFontColor || "rgba(0,0,0,0.8)",
                            border: "2px solid #e0e0e0",
                            borderRadius: "4px",
                            cursor: "pointer",
                            transition: "all 0.2s ease",
                            "&:hover": {
                              transform: "scale(1.05)",
                              boxShadow: "0 2px 6px rgba(0,0,0,0.2)"
                            }
                          }}
                          onClick={(event) => openColorPicker(
                            event,
                            settings.backButtonFontColor || "#000000",
                            "backButtonFontColor",
                            (color) => handleGlobalSettingsChange("backButtonFontColor", color)
                          )}
                          title="Font Color"
                        />
                      </Box>
                    </Box>

                    {/* Next Button */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <TextField
                        label={intl.formatMessage({ id: "survey_builder.next_button" })}
                        value={settings.nextButtonText || intl.formatMessage({ id: "survey_builder.next" })}
                        onChange={(e) => handleGlobalSettingsChange("nextButtonText", e.target.value)}
                        variant="outlined"
                        size="small"
                        sx={{
                          width: "110px",
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "8px",
                            backgroundColor: "white",
                            transition: "all 0.2s ease",
                            "&:hover": {
                              backgroundColor: "white",
                              boxShadow: "0 2px 6px rgba(237, 108, 2, 0.1)",
                            },
                            "&.Mui-focused": {
                              backgroundColor: "white",
                              boxShadow: "0 0 0 2px rgba(237, 108, 2, 0.15)",
                            }
                          },
                          "& .MuiInputLabel-root": {
                            fontSize: "0.8rem"
                          }
                        }}
                      />
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <Box
                          sx={{
                            width: "30px",
                            height: "30px",
                            backgroundColor: settings.nextButtonBackgroundColor || "rgba(255,255,255,0.9)",
                            border: "2px solid #e0e0e0",
                            borderRadius: "4px",
                            cursor: "pointer",
                            transition: "all 0.2s ease",
                            "&:hover": {
                              transform: "scale(1.05)",
                              boxShadow: "0 2px 6px rgba(0,0,0,0.2)"
                            }
                          }}
                          onClick={(event) => openColorPicker(
                            event,
                            settings.nextButtonBackgroundColor || "#ffffff",
                            "nextButtonBackgroundColor",
                            (color) => handleGlobalSettingsChange("nextButtonBackgroundColor", color)
                          )}
                          title="Background Color"
                        />
                        <Box
                          sx={{
                            width: "30px",
                            height: "30px",
                            backgroundColor: settings.nextButtonFontColor || "rgba(0,0,0,0.8)",
                            border: "2px solid #e0e0e0",
                            borderRadius: "4px",
                            cursor: "pointer",
                            transition: "all 0.2s ease",
                            "&:hover": {
                              transform: "scale(1.05)",
                              boxShadow: "0 2px 6px rgba(0,0,0,0.2)"
                            }
                          }}
                          onClick={(event) => openColorPicker(
                            event,
                            settings.nextButtonFontColor || "#000000",
                            "nextButtonFontColor",
                            (color) => handleGlobalSettingsChange("nextButtonFontColor", color)
                          )}
                          title="Font Color"
                        />
                      </Box>
                    </Box>

                    {/* Submit Button */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <TextField
                        label={intl.formatMessage({ id: "survey_builder.submit_button" })}
                        value={settings.submitButtonText || intl.formatMessage({ id: "survey_builder.submit" })}
                        onChange={(e) => handleGlobalSettingsChange("submitButtonText", e.target.value)}
                        variant="outlined"
                        size="small"
                        sx={{
                          width: "120px",
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "8px",
                            backgroundColor: "white",
                            transition: "all 0.2s ease",
                            "&:hover": {
                              backgroundColor: "white",
                              boxShadow: "0 2px 6px rgba(237, 108, 2, 0.1)",
                            },
                            "&.Mui-focused": {
                              backgroundColor: "white",
                              boxShadow: "0 0 0 2px rgba(237, 108, 2, 0.15)",
                            }
                          },
                          "& .MuiInputLabel-root": {
                            fontSize: "0.8rem"
                          }
                        }}
                      />
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <Box
                          sx={{
                            width: "30px",
                            height: "30px",
                            backgroundColor: settings.submitButtonBackgroundColor || "rgba(255,255,255,0.9)",
                            border: "2px solid #e0e0e0",
                            borderRadius: "4px",
                            cursor: "pointer",
                            transition: "all 0.2s ease",
                            "&:hover": {
                              transform: "scale(1.05)",
                              boxShadow: "0 2px 6px rgba(0,0,0,0.2)"
                            }
                          }}
                          onClick={(event) => openColorPicker(
                            event,
                            settings.submitButtonBackgroundColor || "#ffffff",
                            "submitButtonBackgroundColor",
                            (color) => handleGlobalSettingsChange("submitButtonBackgroundColor", color)
                          )}
                          title="Background Color"
                        />
                        <Box
                          sx={{
                            width: "30px",
                            height: "30px",
                            backgroundColor: settings.submitButtonFontColor || "rgba(0,0,0,0.8)",
                            border: "2px solid #e0e0e0",
                            borderRadius: "4px",
                            cursor: "pointer",
                            transition: "all 0.2s ease",
                            "&:hover": {
                              transform: "scale(1.05)",
                              boxShadow: "0 2px 6px rgba(0,0,0,0.2)"
                            }
                          }}
                          onClick={(event) => openColorPicker(
                            event,
                            settings.submitButtonFontColor || "#000000",
                            "submitButtonFontColor",
                            (color) => handleGlobalSettingsChange("submitButtonFontColor", color)
                          )}
                          title="Font Color"
                        />
                      </Box>
                    </Box>

                    {/* A Modern survey's own: the Record button and the voice orb */}
                    {settings.respondMode === "voice" && (
                      <ModernStyleFields
                        settings={settings}
                        onChange={handleGlobalSettingsChange}
                        openColorPicker={openColorPicker}
                      />
                    )}
                  </Box>
                </Box>

                <Divider sx={{
                  mb: 2,
                  borderColor: "rgba(237, 108, 2, 0.05)",
                  borderWidth: "1px",
                }} />

                <Box sx={{ mb: 2 }}>
                  <Typography variant="body1" sx={{ mb: 1, fontWeight: "500", color: "rgb(237, 108, 2)" }}>
                    <FormattedMessage id="survey_builder.others" />
                  </Typography>
                  {/* Question Styling - All in One Row */}
                  <Box sx={{ display: "flex", alignItems: "flex-end", gap: 3, flexWrap: "wrap", mb: 3 }}>
                    {/* Question Background Color */}
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, minWidth: "120px" }}>
                      <Typography variant="body2" sx={{ color: "#666", fontSize: "0.8rem" }}>
                        <FormattedMessage id="survey_builder.question_background" />
                      </Typography>
                      <Box
                        sx={{
                          width: "50px",
                          height: "35px",
                          backgroundColor: settings.questionBackground || "#ffffff",
                          border: "2px solid #e0e0e0",
                          borderRadius: "6px",
                          cursor: "pointer",
                          transition: "all 0.2s ease",
                          "&:hover": {
                            transform: "scale(1.05)",
                            boxShadow: "0 3px 8px rgba(0,0,0,0.2)"
                          }
                        }}
                        onClick={(event) => openColorPicker(
                          event,
                          settings.questionBackground || "#ffffff",
                          "questionBackground",
                          (color) => handleGlobalSettingsChange("questionBackground", color)
                        )}
                      />
                    </Box>

                    {/* Question Background Opacity */}
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, minWidth: "150px", flexGrow: 1 }}>
                      <Typography variant="body2" sx={{ color: "#666", fontSize: "0.8rem" }}>
                        <FormattedMessage id="survey_builder.background_opacity" values={{ value: Math.round((settings.questionOpacity || 1) * 100) }} />
                      </Typography>
                      <Slider
                        value={settings.questionOpacity || 1}
                        onChange={(e, value) => handleGlobalSettingsChange("questionOpacity", value)}
                        min={0.1}
                        max={1}
                        step={0.1}
                        size="small"
                        sx={{
                          "& .MuiSlider-thumb": {
                            backgroundColor: "#2e7d32",
                            width: 16,
                            height: 16
                          },
                          "& .MuiSlider-track": {
                            backgroundColor: "#2e7d32",
                            height: 4
                          },
                          "& .MuiSlider-rail": {
                            backgroundColor: "#e0e0e0",
                            height: 4
                          }
                        }}
                      />
                    </Box>

                    {/* Question Font Color */}
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, minWidth: "120px" }}>
                      <Typography variant="body2" sx={{ color: "#666", fontSize: "0.8rem" }}>
                        <FormattedMessage id="survey_builder.question_font_color" />
                      </Typography>
                      <Box
                        sx={{
                          width: "50px",
                          height: "35px",
                          backgroundColor: settings.questionFontColor || settings.fontColour || "#000000",
                          border: "2px solid #e0e0e0",
                          borderRadius: "6px",
                          cursor: "pointer",
                          transition: "all 0.2s ease",
                          "&:hover": {
                            transform: "scale(1.05)",
                            boxShadow: "0 3px 8px rgba(0,0,0,0.2)"
                          }
                        }}
                        onClick={(event) => openColorPicker(
                          event,
                          settings.questionFontColor || settings.fontColour || "#000000",
                          "questionFontColor",
                          (color) => handleGlobalSettingsChange("questionFontColor", color)
                        )}
                      />
                    </Box>

                    {/* Choice/Option Font Color */}
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, minWidth: "120px" }}>
                      <Typography variant="body2" sx={{ color: "#666", fontSize: "0.8rem" }}>
                        <FormattedMessage id="survey_builder.choice_font_color" />
                      </Typography>
                      <Box
                        sx={{
                          width: "50px",
                          height: "35px",
                          backgroundColor: settings.choiceFontColor || settings.fontColour || "#000000",
                          border: "2px solid #e0e0e0",
                          borderRadius: "6px",
                          cursor: "pointer",
                          transition: "all 0.2s ease",
                          "&:hover": {
                            transform: "scale(1.05)",
                            boxShadow: "0 3px 8px rgba(0,0,0,0.2)"
                          }
                        }}
                        onClick={(event) => openColorPicker(
                          event,
                          settings.choiceFontColor || settings.fontColour || "#000000",
                          "choiceFontColor",
                          (color) => handleGlobalSettingsChange("choiceFontColor", color)
                        )}
                      />
                    </Box>

                    <Divider vertical sx={{
                      height: "70px",
                      // width: "1px",
                      margin: "0 2px",
                      borderColor: "rgba(237, 108, 2, 0.1)",
                      borderWidth: "1px",
                    }} />

                    {/* Survey Title Font Color */}
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, minWidth: "120px" }}>
                      <Typography variant="body2" sx={{ color: "#666", fontSize: "0.8rem" }}>
                        <FormattedMessage id="survey_builder.title_font_color" />
                      </Typography>
                      <Box
                        sx={{
                          width: "50px",
                          height: "35px",
                          backgroundColor: settings.surveyTitleColor || settings.fontColour || "#000000",
                          border: "2px solid #e0e0e0",
                          borderRadius: "6px",
                          cursor: "pointer",
                          transition: "all 0.2s ease",
                          "&:hover": {
                            transform: "scale(1.05)",
                            boxShadow: "0 3px 8px rgba(0,0,0,0.2)"
                          }
                        }}
                        onClick={(event) => openColorPicker(
                          event,
                          settings.surveyTitleColor || settings.fontColour || "#000000",
                          "surveyTitleColor",
                          (color) => handleGlobalSettingsChange("surveyTitleColor", color)
                        )}
                      />
                    </Box>

                    {/* Survey Description Font Color */}
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, minWidth: "120px" }}>
                      <Typography variant="body2" sx={{ color: "#666", fontSize: "0.8rem" }}>
                        <FormattedMessage id="survey_builder.description_font_color" />
                      </Typography>
                      <Box
                        sx={{
                          width: "50px",
                          height: "35px",
                          backgroundColor: settings.surveyDescriptionColor || settings.fontColour || "#000000",
                          border: "2px solid #e0e0e0",
                          borderRadius: "6px",
                          cursor: "pointer",
                          transition: "all 0.2s ease",
                          "&:hover": {
                            transform: "scale(1.05)",
                            boxShadow: "0 3px 8px rgba(0,0,0,0.2)"
                          }
                        }}
                        onClick={(event) => openColorPicker(
                          event,
                          settings.surveyDescriptionColor || settings.fontColour || "#000000",
                          "surveyDescriptionColor",
                          (color) => handleGlobalSettingsChange("surveyDescriptionColor", color)
                        )}
                      />
                    </Box>
                  </Box>
                </Box>
              </AccordionDetails>
            </Accordion>
          </Box >
        );

      case 2: // Preview tab
        return (
          <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
            {/* Preview Controls */}

            {/* Preview Content */}
            <Box sx={{ flexGrow: 1, overflow: "auto" }}>
              {showSubmissionPreview ? (
                // Submission Success Preview
                <Box sx={{
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  // Use completion background gradient if set, otherwise fall back to survey background
                  ...(settings.completionBackgroundGradient ? {
                    background: `linear-gradient(135deg, ${settings.completionBackgroundGradient.start || DEFAULT_GRADIENT.start}, ${settings.completionBackgroundGradient.end || DEFAULT_GRADIENT.end})`
                  } : {
                    background: settings.backgroundMode === "gradient"
                      ? `linear-gradient(135deg, ${settings.surveyColour?.start || DEFAULT_GRADIENT.start}, ${settings.surveyColour?.end || DEFAULT_GRADIENT.end})`
                      : settings.backgroundMode === "image" && settings.backgroundImage
                        ? `url(${settings.backgroundImage})`
                        : settings.backgroundMode === "single"
                          ? settings.backgroundColor || "#ffffff"
                          : "#f8f9fa",
                    backgroundSize: "cover",
                    backgroundPosition: "center"
                  }),
                  p: 4
                }}>
                  <Card sx={{
                    maxWidth: "600px",
                    width: "100%",
                    p: 4,
                    textAlign: "center",
                    borderRadius: "16px",
                    boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
                    background: settings.completionCardBackgroundColor || "rgba(255, 255, 255, 0.95)"
                  }}>
                    {settings.logo && (
                      <Box sx={{ mb: 3, display: "flex", justifyContent: "center" }}>
                        <img
                          src={settings.logo}
                          alt="Logo"
                          style={{ maxWidth: "150px", maxHeight: "80px", objectFit: "contain" }}
                        />
                      </Box>
                    )}
                    <Typography
                      variant="h4"
                      sx={{
                        color: settings.completionTextColor || "#2e7d32",
                        fontWeight: "bold",
                        mb: 2
                      }}
                    >
                      {settings.successTitle || intl.formatMessage({ id: "survey_builder.default_success_title", defaultMessage: "Thank you!" })}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: settings.completionTextColor || "#2e7d32",
                        mb: 3,
                        lineHeight: 1.6
                      }}
                    >
                      {settings.successDescription || intl.formatMessage({ id: "survey_builder.default_success_description", defaultMessage: "Your response has been recorded successfully." })}
                    </Typography>
                    {settings.endMessage && (
                      <Typography
                        variant="body2"
                        sx={{
                          color: settings.completionTextColor || "#2e7d32",
                          fontStyle: "italic",
                          opacity: 0.8
                        }}
                      >
                        {settings.endMessage}
                      </Typography>
                    )}
                    {settings.redirectLink && (
                      <Box sx={{ mt: 3 }}>
                        <Typography variant="body2" sx={{ color: "#666", mb: 1 }}>
                          {intl.formatMessage({ id: "survey_builder.redirecting_to", defaultMessage: "Redirecting to:" })} {settings.redirectLink}
                        </Typography>
                        <Button
                          variant="outlined"
                          sx={{
                            color: settings.completionTextColor || "#2e7d32",
                            borderColor: settings.completionTextColor || "#2e7d32"
                          }}
                        >
                          {intl.formatMessage({ id: "survey_builder.continue", defaultMessage: "Continue" })}
                        </Button>
                      </Box>
                    )}
                  </Card>
                </Box>
              ) : (
                // Regular Survey Preview (a Modern assessment previews as its runner)
                <SurveyPreviewSection steps={steps} settings={settings} />
              )}
            </Box>
          </Box>
        );



      default:
        return null;
    }
  };

  return (
    <Box sx={{
      height: { xs: "100vh", sm: "80vh" }, // Full height on mobile, 90% on larger screens
      width: "95%",
      maxWidth: "1800px", // Reasonable maximum width for large screens
      margin: "0 auto", // Center the container
      p: { xs: 1, sm: 2, md: 3, lg: 4 }, // Responsive padding
      display: "flex",
      flexDirection: "column",
      background: "linear-gradient(135deg, #f8f9fa 0%, #e8f4fd 100%)",
      overflow: "hidden",
      position: "relative",
      borderRadius: "16px",
      boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
      "&::before": {
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: `
          radial-gradient(circle at 20% 80%, rgba(25, 118, 210, 0.05) 0%, transparent 50%),
          radial-gradient(circle at 80% 20%, rgba(46, 125, 50, 0.05) 0%, transparent 50%),
          radial-gradient(circle at 40% 40%, rgba(237, 108, 2, 0.03) 0%, transparent 50%)
        `,
        pointerEvents: "none",
        borderRadius: "16px",
      }
    }}>
      {/* Tab Navigation */}
      <Box sx={{
        borderBottom: 1,
        borderColor: 'divider',
        background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        position: 'relative',
        borderRadius: '16px 16px 0 0',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '1px',
          background: 'linear-gradient(90deg, transparent 0%, rgba(25, 118, 210, 0.2) 50%, transparent 100%)',
        }
      }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{
            minHeight: '70px',
            borderRadius: '16px 16px 0 0',
            '& .MuiTab-root': {
              textTransform: 'none',
              fontSize: { xs: '0.9rem', sm: '1rem' },
              fontWeight: '600',
              minHeight: '70px',
              borderRadius: '12px 12px 0 0',
              margin: { xs: '0 2px', sm: '0 4px' },
              padding: { xs: '12px 16px', sm: '16px 24px' },
              position: 'relative',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: '50%',
                transform: 'translateX(-50%)',
                width: '0%',
                height: '3px',
                background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                transition: 'width 0.3s ease',
              },
              '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.05)',
                transform: 'translateY(-2px)',
                '&::before': {
                  width: '60%',
                }
              },
              '&.Mui-selected': {
                background: 'linear-gradient(135deg, #e3f2fd 0%, rgba(25, 118, 210, 0.08) 100%)',
                color: '#1976d2',
                boxShadow: '0 4px 12px rgba(25, 118, 210, 0.15)',
                transform: 'translateY(-2px)',
                '&::before': {
                  width: '80%',
                }
              },
            },
            '& .MuiTabs-indicator': {
              display: 'none',
            },
          }}
        >
          <Tab
            // icon={<span style={{ fontSize: '1.2rem', marginRight: '8px' }}>📝</span>}
            label={intl.formatMessage({ id: "survey.basic" })}
            iconPosition="start"
          />
          <Tab
            // icon={<span style={{ fontSize: '1.2rem', marginRight: '8px' }}>⚙️</span>}
            label={intl.formatMessage({ id: "survey.advanced" })}
            iconPosition="start"
          />
          <Tab
            // icon={<span style={{ fontSize: '1.2rem', marginRight: '8px' }}>👁️</span>}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {intl.formatMessage({ id: "survey.preview" })}
                {activeTab === 2 && (
                  <Tooltip title={showSubmissionPreview ? intl.formatMessage({ id: "survey.switch_to_questions_preview" }) : intl.formatMessage({ id: "survey.switch_to_submission_preview" })}>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowSubmissionPreview(!showSubmissionPreview);
                      }}
                      sx={{
                        width: '24px',
                        height: '24px',
                        ml: 0.5,
                        color: showSubmissionPreview ? '#4caf50' : '#757575',
                        backgroundColor: showSubmissionPreview ? 'rgba(76, 175, 80, 0.1)' : 'rgba(0, 0, 0, 0.05)',
                        '&:hover': {
                          backgroundColor: showSubmissionPreview ? 'rgba(76, 175, 80, 0.2)' : 'rgba(0, 0, 0, 0.1)',
                          transform: 'scale(1.1)',
                        },
                        transition: 'all 0.2s ease',
                      }}
                    >
                      {showSubmissionPreview ? '📋' : '❓'}
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            }
            iconPosition="start"
          />
        </Tabs>
      </Box>

      {/* Main Content Card */}
      <Card sx={{
        flexGrow: 1,
        borderRadius: '0 0 16px 16px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        overflow: 'auto',
        border: '1px solid rgba(0,0,0,0.08)',
        background: 'linear-gradient(135deg, #ffffff 0%, #fafbfc 100%)',
        margin: { xs: 0, sm: '0 8px', md: '0 16px' },
        borderRadius: { xs: '0 0 12px 12px', sm: '0 0 16px 16px' },
      }}>
        <Box sx={{
          ...(activeTab !== 1 && {
            height: '100%',
            overflow: 'auto',
          }),
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 25%, #2e7d32 50%, #d32f2f 75%, #ed6c02 100%)',
            zIndex: 1,
          }
        }}>
          {renderTabContent()}
        </Box>
      </Card>

      {/* Dialogs */}
      <Dialog open={openBulkAddDialog} onClose={handleCloseBulkAddDialog}>
        <DialogTitle>{intl.formatMessage({ id: "survey.survey_builder.bulk_add_questions_dialog_title" })}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mb: 2, mt: 1 }}>
            <InputLabel>{intl.formatMessage({ id: "survey.survey_builder.question_type_label" })}</InputLabel>
            <Select
              value={bulkAddType}
              onChange={(e) => setBulkAddType(e.target.value)}
              label={intl.formatMessage({ id: "survey.survey_builder.question_type_label" })}
            >
              {questionTypes.map((qt) => (
                <MenuItem key={qt.id} value={qt.type}>
                  {getQuestionTypeLabel(intl, qt)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label={intl.formatMessage({ id: "survey.survey_builder.number_of_questions_label" })}
            type="number"
            value={bulkAddCount}
            onChange={(e) => setBulkAddCount(Math.min(100, Math.max(1, parseInt(e.target.value) || 1)))}
            fullWidth
            inputProps={{ min: 1, max: 100 }}
            sx={{ mb: 2 }}
          />
          <TextField
            label={intl.formatMessage({ id: "survey.survey_builder.default_question_text_label" })}
            value={defaultQuestionText}
            onChange={(e) => setDefaultQuestionText(e.target.value)}
            fullWidth
            placeholder={intl.formatMessage({ id: "survey.survey_builder.default_question_text_placeholder" })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseBulkAddDialog}>{intl.formatMessage({ id: "survey.survey_builder.cancel_button" })}</Button>
          <Button
            onClick={handleBulkAddQuestions}
            variant="contained"
            disabled={!bulkAddType || bulkAddCount < 1 || bulkAddCount > 100}
          >
            {intl.formatMessage({ id: "survey.survey_builder.add_questions_button" })}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openValidationDialog} onClose={() => setOpenValidationDialog(false)}>
        <DialogTitle>{intl.formatMessage({ id: "survey.survey_builder.invalid_questions_dialog_title" })}</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            {intl.formatMessage({ id: "survey.survey_builder.upload_issues_message" })}
          </Typography>
          <Box sx={{ maxHeight: '300px', overflowY: 'auto', mt: 2 }}>
            {invalidQuestions.map((error, idx) => (
              <Box key={idx} sx={{ mb: 1, p: 1, border: '1px solid red', borderRadius: 1 }}>
                <Typography variant="body2">
                  <strong>{error.index === 0 ? intl.formatMessage({ id: "survey.survey_builder.file_label" }) : `${intl.formatMessage({ id: "survey.survey_builder.question_label" })} ${error.index}`}:</strong> {error.question.replace(/<\/?[^>]+(>|$)/g, '')}
                </Typography>
                <Typography variant="body2" color="error">
                  {error.reason}
                </Typography>
              </Box>
            ))}
          </Box>
          <Typography sx={{ mt: 2 }}>
            {intl.formatMessage({ id: "survey.survey_builder.proceed_upload_message" })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setOpenValidationDialog(false);
            setInvalidQuestions([]);
            setPendingFile(null);
            setAcceptedFiles([]);
          }}>
            {intl.formatMessage({ id: "survey.survey_builder.cancel_button" })}
          </Button>
          <Button
            onClick={handleProceedWithUpload}
            color="primary"
            variant="contained"
          >
            {intl.formatMessage({ id: "survey.survey_builder.okay_button" })}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openClearConfirmationDialog} onClose={handleCancelUpload}>
        <DialogTitle>{intl.formatMessage({ id: "survey.confirm_clear_questions_title" })}</DialogTitle>
        <DialogContent>
          <Typography>
            {intl.formatMessage({ id: "survey.confirm_clear_questions" })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelUpload}>{intl.formatMessage({ id: "survey.survey_builder.cancel_button" })}</Button>
          <Button
            onClick={handleConfirmClearAndUpload}
            color="primary"
            variant="contained"
          >
            {intl.formatMessage({ id: "survey.survey_builder.okay_button" })}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Custom Color Picker Popover */}
      <Popover
        open={colorPickerState.open}
        anchorEl={colorPickerState.anchorEl}
        onClose={closeColorPicker}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        sx={{
          '& .MuiPopover-paper': {
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
            border: '1px solid rgba(0,0,0,0.1)',
            overflow: 'visible',
            mt: 1,
          }
        }}
        TransitionProps={{
          timeout: {
            enter: 200,
            exit: 150
          }
        }}
      >
        <ClickAwayListener onClickAway={closeColorPicker}>
          <Box sx={{ p: 2, width: 150, height: 150, overflowY: 'auto' }}>

            {/* Action Buttons */}

            {/* Color Preview */}
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              mb: 2
            }}>
              {/* HTML5 Color Input - Styled directly */}
              <input
                type="color"
                value={colorPickerState.currentColor}
                onChange={(e) => handleColorChange(e.target.value)}
                style={{
                  width: '40px',
                  height: '40px',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  padding: 0,
                  backgroundColor: 'transparent',
                  flexShrink: 0,
                  outline: 'none',
                  WebkitAppearance: 'none',
                  MozAppearance: 'none',
                  appearance: 'none',
                  // Remove all default browser styling
                  WebkitBorderRadius: '8px',
                  MozBorderRadius: '8px',
                  // Ensure no inner borders or shadows
                  boxShadow: 'none',
                  WebkitBoxShadow: 'none',
                  MozBoxShadow: 'none'
                }}
              />
              <TextField
                value={colorPickerState.currentColor}
                onChange={(e) => handleColorChange(e.target.value)}
                size="small"
                label="Hex Color"
                sx={{ flexGrow: 1 }}
              />
            </Box>

            {/* Quick Color Presets */}
            <Typography variant="caption" sx={{ mb: 1, display: 'block', color: '#666' }}>
              Quick Colors
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              {[
                '#ffffff', '#000000', '#f44336', '#e91e63', '#9c27b0', '#673ab7',
                '#3f51b5', '#2196f3', '#03a9f4', '#00bcd4', '#009688', '#4caf50',
                '#8bc34a', '#cddc39', '#ffeb3b', '#ffc107', '#ff9800', '#ff5722'
              ].map((color) => (
                <Box
                  key={color}
                  sx={{
                    width: 24,
                    height: 24,
                    backgroundColor: color,
                    border: colorPickerState.currentColor === color ? '2px solid #1976d2' : '1px solid #e0e0e0',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      transform: 'scale(1.1)',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                    }
                  }}
                  onClick={() => handleColorChange(color)}
                />
              ))}
            </Box>
          </Box>
        </ClickAwayListener>
      </Popover>

      {/* Background Image Modal */}
      <Modal
        open={imageModalOpen}
        onClose={() => setImageModalOpen(false)}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
      >
        <Fade in={imageModalOpen}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              maxWidth: '90vw',
              maxHeight: '90vh',
              bgcolor: 'background.paper',
              borderRadius: '16px',
              boxShadow: '0 24px 48px rgba(0,0,0,0.3)',
              p: 3,
              outline: 'none',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2
            }}
          >
            <Typography variant="h6" sx={{ color: '#333', fontWeight: 600 }}>
              Background Image Preview
            </Typography>

            <Box
              sx={{
                borderRadius: '12px',
                overflow: 'hidden',
                boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
                maxWidth: '100%',
                maxHeight: 'calc(90vh - 120px)'
              }}
            >
              <img
                src={settings.backgroundImage}
                alt="Background Image"
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain',
                  display: 'block'
                }}
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
              <Button
                variant="outlined"
                onClick={() => setImageModalOpen(false)}
                sx={{
                  borderRadius: '8px',
                  textTransform: 'none'
                }}
              >
                Close
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={() => {
                  handleGlobalSettingsChange("backgroundImage", null);
                  setImageModalOpen(false);
                }}
                sx={{
                  borderRadius: '8px',
                  textTransform: 'none'
                }}
              >
                Remove Image
              </Button>
            </Box>
          </Box>
        </Fade>
      </Modal>

      {/* CSV Upload Guide Modal */}
      <Modal
        open={csvGuideModalOpen}
        onClose={() => setCsvGuideModalOpen(false)}
        closeAfterTransition
        BackdropProps={{
          timeout: 500,
          sx: { backgroundColor: 'rgba(0, 0, 0, 0.7)' }
        }}
      >
        <Fade in={csvGuideModalOpen}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: { xs: '90%', sm: '80%', md: '600px' },
              bgcolor: 'background.paper',
              borderRadius: '16px',
              boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
              p: 4,
              maxHeight: '80vh',
              overflow: 'auto'
            }}
          >
            <Typography variant="h5" sx={{ mb: 3, fontWeight: 'bold', color: '#1976d2' }}>
              CSV Upload Guide
            </Typography>

            {(() => {
              // Example CSV data
              const exampleData = [
                { question: "Select your favorite colors", choices: "Red, Blue, Green", type: "" },
                { question: "What is your favorite color?", choices: "Red, A longer color with \\, in it, Green", type: "single_choice" },
                { question: "", choices: "", type: "" },
                { question: "How satisfied are you?", choices: "Very Satisfied, Satisfied, Neutral, Dissatisfied, Very Dissatisfied", type: "" },
                { question: "Tell us about yourself", choices: "", type: "open_text" },
                { question: "Welcome to our assessment", choices: "", type: "text" }
              ];

              // Process the example data to show extracted results
              const processedData = exampleData.map((row, index) => {
                // Check if question is blank - treat as page break
                const questionText = (row.question || '').trim();
                if (!questionText) {
                  return {
                    question: '(blank = Page Break)',
                    extractedChoices: [],
                    detectedType: 'PAGE_BREAK'
                  };
                }
                const rawChoices = parseChoices(row.choices);
                const questionType = determineQuestionType(row.type, rawChoices);
                return {
                  ...row,
                  extractedChoices: rawChoices,
                  detectedType: questionType
                };
              });

              return (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#333' }}>
                    Example CSV Data
                  </Typography>

                  {/* CSV Table */}
                  <Box sx={{
                    mb: 3,
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    overflow: 'auto',
                    maxHeight: '200px',
                    backgroundColor: '#fff'
                  }}>
                    <Box sx={{ minWidth: '800px' }}>
                      {/* Header */}
                      <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: '2fr 2fr 1fr',
                        backgroundColor: '#f5f5f5',
                        borderBottom: '1px solid #ddd',
                        position: 'sticky',
                        top: 0,
                        zIndex: 1
                      }}>
                        <Box sx={{ p: 1, fontWeight: 'bold', borderRight: '1px solid #ddd', fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          question*
                          <Tooltip title="Write the question text. Leave blank to create a page break." arrow>
                            <Box sx={{
                              width: 14,
                              height: 14,
                              borderRadius: '50%',
                              backgroundColor: '#1976d2',
                              color: 'white',
                              fontSize: '0.7rem',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'help'
                            }}>
                              i
                            </Box>
                          </Tooltip>
                        </Box>
                        <Box sx={{ p: 1, fontWeight: 'bold', borderRight: '1px solid #ddd', fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          choices
                          <Tooltip title='Separate choices with "," and if the choice contains comma, use "\,".' arrow>
                            <Box sx={{
                              width: 14,
                              height: 14,
                              borderRadius: '50%',
                              backgroundColor: '#1976d2',
                              color: 'white',
                              fontSize: '0.7rem',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'help'
                            }}>
                              i
                            </Box>
                          </Tooltip>
                        </Box>
                        <Box sx={{ p: 1, fontWeight: 'bold', fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          type
                          <Tooltip
                            title={
                              <Box sx={{ fontSize: '0.7rem' }}>
                                <Typography sx={{ fontSize: '0.7rem' }}>
                                  Available types: single_choice, multi_choice, open_text, text, likert_scale.
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 0.5 }}>
                                  <Typography
                                    sx={{
                                      color: 'rgb(148, 196, 245)',
                                      textDecoration: 'underline',
                                      cursor: 'pointer',
                                      '&:hover': { color: '#1565c0' },
                                      fontSize: '0.7rem'
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setLikertScalesModalOpen(true);
                                    }}
                                  >
                                    Pre-approved Likert choices
                                  </Typography>
                                  <Typography sx={{ fontSize: '0.7rem' }}>
                                    {' '}does not require a type.
                                  </Typography>
                                </Box>
                              </Box>
                            }
                            arrow
                          >
                            <Box sx={{
                              width: 14,
                              height: 14,
                              borderRadius: '50%',
                              backgroundColor: '#1976d2',
                              color: 'white',
                              fontSize: '0.7rem',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'help'
                            }}>
                              i
                            </Box>
                          </Tooltip>
                        </Box>
                      </Box>

                      {/* Data Rows */}
                      {exampleData.map((row, index) => (
                        <Box key={index} sx={{
                          display: 'grid',
                          gridTemplateColumns: '2fr 2fr 1fr',
                          borderBottom: index < exampleData.length - 1 ? '1px solid #eee' : 'none',
                          '&:hover': { backgroundColor: '#f9f9f9' }
                        }}>
                          <Box sx={{
                            p: 1,
                            borderRight: '1px solid #eee',
                            fontSize: '0.8rem',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {row.question}
                          </Box>
                          <Box sx={{
                            p: 1,
                            borderRight: '1px solid #eee',
                            fontSize: '0.8rem',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {row.choices}
                          </Box>
                          <Box sx={{
                            p: 1,
                            fontSize: '0.8rem',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {row.type}
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </Box>

                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#333' }}>
                    Extracted Results
                  </Typography>

                  {/* Results Table */}
                  <Box sx={{
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    overflow: 'auto',
                    maxHeight: '200px',
                    backgroundColor: '#fff'
                  }}>
                    <Box sx={{ minWidth: '900px' }}>
                      {/* Header */}
                      <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: '2fr 2fr 1fr 1fr',
                        backgroundColor: '#e8f5e8',
                        borderBottom: '1px solid #ddd',
                        position: 'sticky',
                        top: 0,
                        zIndex: 1
                      }}>
                        <Box sx={{ p: 1, fontWeight: 'bold', borderRight: '1px solid #ddd', fontSize: '0.875rem' }}>
                          Question
                        </Box>
                        <Box sx={{ p: 1, fontWeight: 'bold', borderRight: '1px solid #ddd', fontSize: '0.875rem' }}>
                          Parsed Choices
                        </Box>
                        <Box sx={{ p: 1, fontWeight: 'bold', borderRight: '1px solid #ddd', fontSize: '0.875rem' }}>
                          Detected Type
                        </Box>
                        <Box sx={{ p: 1, fontWeight: 'bold', fontSize: '0.875rem' }}>
                          Status
                        </Box>
                      </Box>

                      {/* Data Rows */}
                      {processedData.map((row, index) => (
                        <Box key={index} sx={{
                          display: 'grid',
                          gridTemplateColumns: '2fr 2fr 1fr 1fr',
                          borderBottom: index < processedData.length - 1 ? '1px solid #eee' : 'none',
                          '&:hover': { backgroundColor: '#f9f9f9' }
                        }}>
                          <Box sx={{
                            p: 1,
                            borderRight: '1px solid #eee',
                            fontSize: '0.8rem',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {row.question}
                          </Box>
                          <Box sx={{
                            p: 1,
                            borderRight: '1px solid #eee',
                            fontSize: '0.8rem',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {row.extractedChoices.length > 0 ? `["${row.extractedChoices.join('", "')}"]` : 'None'}
                          </Box>
                          <Box sx={{
                            p: 1,
                            borderRight: '1px solid #eee',
                            fontSize: '0.8rem',
                            color: row.detectedType === 'LIKERT_SCALE' ? '#ff9800' :
                              row.detectedType === 'SINGLE_CHOICE' ? '#0B4472' :
                                row.detectedType === 'MULTI_CHOICE' ? '#03a9f4' :
                                  row.detectedType === 'OPEN_TEXT' ? '#4caf50' :
                                    row.detectedType === 'TEXT' ? '#9c27b0' : '#607d8b',
                            fontWeight: 'bold'
                          }}>
                            {row.detectedType}
                          </Box>
                          <Box sx={{
                            p: 1,
                            fontSize: '0.8rem',
                            color: '#4caf50',
                            fontWeight: 'bold'
                          }}>
                            ✓ Valid
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Box>
              );
            })()}

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                onClick={() => setCsvGuideModalOpen(false)}
                sx={{ borderRadius: '8px', textTransform: 'none' }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  setCsvGuideModalOpen(false);
                  // Trigger file picker
                  const input = document.createElement('input');
                  input.type = 'file';
                  input.accept = '.csv';
                  input.onchange = (e) => {
                    const file = e.target.files[0];
                    if (file) {
                      if (steps.length > 0 || processedFileName) {
                        setFileToProcess(file);
                        setOpenClearConfirmationDialog(true);
                      } else {
                        processFile(file);
                      }
                    }
                  };
                  input.click();
                }}
                sx={{
                  borderRadius: '8px',
                  textTransform: 'none',
                  backgroundColor: '#1976d2',
                  '&:hover': {
                    backgroundColor: '#1565c0'
                  }
                }}
              >
                📂 Choose CSV File
              </Button>
            </Box>
          </Box>
        </Fade>
      </Modal>

      {/* Likert Scales Reference Modal */}
      <Modal
        open={likertScalesModalOpen}
        onClose={() => setLikertScalesModalOpen(false)}
        closeAfterTransition
        BackdropProps={{
          timeout: 500,
          sx: { backgroundColor: 'rgba(0, 0, 0, 0.7)' }
        }}
      >
        <Fade in={likertScalesModalOpen}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: { xs: '90%', sm: '80%', md: '500px' },
              bgcolor: 'background.paper',
              borderRadius: '16px',
              boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
              p: 4,
              maxHeight: '70vh',
              overflow: 'auto'
            }}
          >
            <Typography variant="h5" sx={{ mb: 3, fontWeight: 'bold', color: '#1976d2' }}>
              Pre-approved Likert Scale Choices
            </Typography>

            <Typography variant="body2" sx={{ mb: 3, color: '#666' }}>
              If your CSV choices exactly match any of these scales, the question type will automatically be detected as Likert Scale:
            </Typography>

            <Box sx={{ maxHeight: '400px', overflow: 'auto', pr: 1 }}>
              {likertScaleTypes
                .filter(scale => scale.id !== 'CUSTOM') // Exclude custom scale
                .map((scale, index) => (
                  <Box
                    key={scale.id}
                    sx={{
                      mb: 3,
                      p: 2,
                      border: '1px solid #e0e0e0',
                      borderRadius: '8px',
                      backgroundColor: '#fafafa'
                    }}
                  >
                    <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold', color: '#333' }}>
                      {scale.label}
                    </Typography>
                    <Box sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 0.5,
                      mt: 1
                    }}>
                      {scale.options.map((option, optionIndex) => (
                        <Box
                          key={optionIndex}
                          sx={{
                            px: 1.5,
                            py: 0.5,
                            backgroundColor: '#e3f2fd',
                            borderRadius: '16px',
                            fontSize: '0.8rem',
                            color: '#1976d2',
                            border: '1px solid #bbdefb'
                          }}
                        >
                          {option}
                        </Box>
                      ))}
                    </Box>
                    <Typography variant="caption" sx={{ mt: 1, display: 'block', color: '#666' }}>
                      CSV format: {scale.options.join(', ')}
                    </Typography>
                  </Box>
                ))}
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button
                variant="contained"
                onClick={() => setLikertScalesModalOpen(false)}
                sx={{
                  borderRadius: '8px',
                  textTransform: 'none',
                  backgroundColor: '#1976d2',
                  '&:hover': {
                    backgroundColor: '#1565c0'
                  }
                }}
              >
                Got it!
              </Button>
            </Box>
          </Box>
        </Fade>
      </Modal>

      {/* CSV Import Confirmation Modal */}
      <Modal
        open={csvConfirmationModalOpen}
        onClose={handleCancelCsvImport}
        closeAfterTransition
        BackdropProps={{
          timeout: 500,
          sx: { backgroundColor: 'rgba(0, 0, 0, 0.7)' }
        }}
      >
        <Fade in={csvConfirmationModalOpen}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: { xs: '95%', sm: '90%', md: '700px' },
              bgcolor: 'background.paper',
              borderRadius: '16px',
              boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
              p: 4,
              maxHeight: '80vh',
              overflow: 'auto'
            }}
          >
            {csvImportData && (
              <>
                <Typography variant="h5" sx={{ mb: 3, fontWeight: 'bold', color: '#1976d2' }}>
                  Confirm CSV Import
                </Typography>

                {/* Import Summary */}
                <Box sx={{ mb: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
                  <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#333' }}>
                      Import Summary
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2, fontWeight: 'bold' }}>
                      {csvImportData.totalCount} questions
                    </Typography>

                  </Box>

                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {Object.entries(csvImportData.typeCounts).map(([type, count]) => {
                      const typeColors = {
                        'SINGLE_CHOICE': 'rgba(0, 255, 98, 0.26)',
                        'MULTI_CHOICE': 'rgba(0, 225, 255, 0.27)',
                        'LIKERT_SCALE': 'rgba(161, 15, 95, 0.19)',
                        'OPEN_TEXT': 'rgba(72, 0, 255, 0.19)',
                        'TEXT': 'rgba(221, 255, 0, 0.62)'
                      };
                      const typeLabels = {
                        'SINGLE_CHOICE': 'Single Choice',
                        'MULTI_CHOICE': 'Multi Choice',
                        'LIKERT_SCALE': 'Likert Scale',
                        'OPEN_TEXT': 'Open Text',
                        'TEXT': 'Content'
                      };

                      return (
                        <Box
                          key={type}
                          sx={{
                            px: 2,
                            py: 0.5,
                            backgroundColor: typeColors[type] || '#666',
                            color: '#555',
                            borderRadius: '16px',
                            fontSize: '0.75rem',
                            fontWeight: 'bold'
                          }}
                        >
                          {typeLabels[type] || type}: {count}
                        </Box>
                      );
                    })}
                  </Box>
                </Box>

                {/* Questions Preview */}
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#333' }}>
                  Questions Preview
                </Typography>
                <Box sx={{
                  maxHeight: '400px',
                  overflow: 'auto',
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  backgroundColor: '#fafafa'
                }}>
                  {csvImportData.questions.map((question, index) => (
                    <Box
                      key={question.id}
                      sx={{
                        borderBottom: index < csvImportData.questions.length - 1 ? '1px solid #e0e0e0' : 'none'
                      }}
                    >
                      {/* Question Header */}
                      <Box
                        sx={{
                          p: 2,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          backgroundColor: expandedQuestions[question.id] ? '#fff' : 'transparent',
                          '&:hover': { backgroundColor: '#f0f0f0' }
                        }}
                        onClick={() => question.type !== 'TEXT' && question.type !== 'OPEN_TEXT' && question.type !== 'PAGE_BREAK' && toggleQuestionExpansion(question.id)}
                      >
                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'row', justifyContent: 'space-between', mr: 1 }}>
                          <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 0.5, textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                            {index + 1}. {question.type === 'PAGE_BREAK' ? '--- Page Break ---' : stripHtml(question.question)}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box
                              sx={{
                                px: 1.5,
                                py: 0.3,
                                backgroundColor:
                                  question.type === 'PAGE_BREAK' ? 'rgba(255, 152, 0, 0.19)' :
                                    question.type === 'LIKERT_SCALE' ? 'rgba(161, 15, 95, 0.19)' :
                                      question.type === 'OPEN_TEXT' ? 'rgba(72, 0, 255, 0.19)' :
                                        question.type === 'TEXT' ? 'rgba(221, 255, 0, 0.62)' :
                                          question.type === 'MULTI_CHOICE' ? 'rgba(0, 225, 255, 0.27)' :
                                            'rgba(0, 255, 98, 0.26)',
                                color: '#555',
                                borderRadius: '12px',
                                fontSize: '0.7rem',
                                fontWeight: 'bold'
                              }}
                            >
                              {question.type.replace('_', ' ')}
                            </Box>
                            {question.choices && question.choices.length > 0 && (
                              <Typography variant="caption" sx={{ color: '#666' }}>
                                {question.choices.length} choices
                              </Typography>
                            )}
                          </Box>
                        </Box>
                        {question.type !== 'TEXT' && question.type !== 'OPEN_TEXT' && <Box sx={{
                          transform: expandedQuestions[question.id] ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 0.2s'
                        }}>
                          ▼
                        </Box>
                        }
                      </Box>

                      {/* Question Details */}
                      {expandedQuestions[question.id] && (
                        <Box sx={{ p: 2, pt: 0, backgroundColor: '#fff' }}>
                          {question.description && (
                            <Typography variant="body2" sx={{ mb: 1, color: '#666', fontStyle: 'italic' }}>
                              Description: {stripHtml(question.description)}
                            </Typography>
                          )}
                          {question.choices && question.choices.length > 0 && (
                            <Box>
                              <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold', color: '#333' }}>
                                Choices:
                              </Typography>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {question.choices.map((choice, choiceIndex) => (
                                  <Box
                                    key={choiceIndex}
                                    sx={{
                                      px: 1,
                                      py: 0.3,
                                      backgroundColor: '#e3f2fd',
                                      borderRadius: '8px',
                                      fontSize: '0.8rem',
                                      color: '#1976d2',
                                      border: '1px solid #bbdefb'
                                    }}
                                  >
                                    {choice.text}
                                  </Box>
                                ))}
                              </Box>
                            </Box>
                          )}
                        </Box>
                      )}
                    </Box>
                  ))}
                </Box>

                {/* Action Buttons */}
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
                  <Button
                    variant="outlined"
                    onClick={handleCancelCsvImport}
                    sx={{ borderRadius: '8px', textTransform: 'none' }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleConfirmCsvImport}
                    sx={{
                      borderRadius: '8px',
                      textTransform: 'none',
                      backgroundColor: '#1976d2',
                      '&:hover': {
                        backgroundColor: '#1565c0'
                      }
                    }}
                  >
                    Import {csvImportData.totalCount} Questions
                  </Button>
                </Box>
              </>
            )}
          </Box>
        </Fade>
      </Modal>
    </Box>
  );
}

export default SurveyBuilder;
